import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { FaStar } from 'react-icons/fa';
import { createPortal } from 'react-dom';
import './PropertyDetails.css';
import UnitHistory from './UnitHistory';
import LocationPicker from './LocationPicker';
import { getPropertyById, updateProperty, uploadPropertyImage, uploadAgreementDocument, createUnit, uploadUnitImage, createBulkUnits, getPropertyUnits, updateUnit, deleteUnit, assignTenantToUnit, getPropertyUnitTenancyHistory, getManageBnbBookings, getRentStatus } from '../../../api/services/propertyService';
import RentStatusDetails from './RentStatusDetails';
import { issueEvictionNotice, releaseTenant } from '../../../api/services/rentalService';
import { getUnitTypes, getUnitCategories, getUnitSubcategories } from '../../../api/services/adminService';
import { fetchAuthenticatedImage } from '../../../api/services/authService';
import { storeImageInCache, getImageFromCache, BROKEN_IMAGE_PLACEHOLDER_SRC } from '../../../utils/imageCache';
import { setPropertyScope } from '../../../utils/propertyScope';
import { computePropertyUnitStats } from '../../../utils/propertyUnitStats';
import { filterUnitsByListingScope } from '../../../utils/publicListingScope';
import {
  normalizeUnitFromApi,
  hydrateUnitsWithActiveTenancy as hydrateUnitsWithTenancy,
} from '../../../utils/propertyUnitTenancy';
import TenantRatingModal from '../../shared/TenantRatingModal/TenantRatingModal';

/** History marker so browser Back exits unit history first. */
const UNIT_HISTORY_HISTORY_KEY = 'tenantscore_unitHistory';

const UNIT_FILTER_OPTIONS = [
  { value: 'all', label: 'All Units' },
  { value: 'vacant', label: 'Vacant' },
  { value: 'occupied', label: 'Occupied' },
  { value: 'private', label: 'Private' },
  { value: 'public', label: 'Public' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
];

function buildUnitPaymentStatusLookup(rentData) {
  const lookup = new Map();
  const items = Array.isArray(rentData?.items) ? rentData.items : [];
  for (const item of items) {
    const status = String(item.paymentStatus || '').toUpperCase();
    if (!status) continue;
    const unitId = item.unitId != null ? String(item.unitId).trim() : '';
    if (unitId) lookup.set(unitId, status);
    const unitNumber = item.unitNumber != null ? String(item.unitNumber).trim().toLowerCase() : '';
    if (unitNumber) lookup.set(`num:${unitNumber}`, status);
  }
  return lookup;
}

function getUnitPaymentStatus(unit, paymentStatusLookup) {
  if (!paymentStatusLookup?.size) return null;
  const unitId = getUnitCanonicalId(unit);
  if (unitId && paymentStatusLookup.has(unitId)) {
    return paymentStatusLookup.get(unitId);
  }
  const unitNumber = unit.unitNumber != null ? String(unit.unitNumber).trim().toLowerCase() : '';
  if (unitNumber && paymentStatusLookup.has(`num:${unitNumber}`)) {
    return paymentStatusLookup.get(`num:${unitNumber}`);
  }
  return null;
}

function getUnitThumbnailUrl(unit) {
  const photos = unit?.photos;
  if (Array.isArray(photos) && photos.length > 0) {
    const url = photos[0];
    if (url && (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:'))) {
      return url;
    }
  }
  if (unit?.imageUrl) return unit.imageUrl;
  return null;
}

function formatUnitSubtitle(unit) {
  if (unit?.description?.trim()) return unit.description.trim();
  if (unit?.unitTypeName) return unit.unitTypeName;
  const beds = unit?.bedrooms;
  if (beds) return `${beds} Bedroom${Number(beds) === 1 ? '' : 's'}`;
  return '';
}

function formatUnitTitle(unit) {
  if (unit?.name?.trim()) return unit.name.trim();
  if (unit?.unitNumber) return `Unit ${unit.unitNumber}`;
  return 'Unit';
}

function extractRentPaymentCounts(rentData) {
  if (!rentData) return { paid: 0, unpaid: 0, partial: 0 };

  const paidFromApi = rentData.paidCount ?? rentData.paidUnits ?? rentData.paid;
  const unpaidFromApi = rentData.unpaidCount ?? rentData.unpaidUnits ?? rentData.unpaid;
  const partialFromApi = rentData.partialCount ?? rentData.partialUnits ?? rentData.partial;

  if (
    [paidFromApi, unpaidFromApi, partialFromApi].every((v) => v !== undefined && v !== null && Number.isFinite(Number(v)))
  ) {
    return {
      paid: Number(paidFromApi),
      unpaid: Number(unpaidFromApi),
      partial: Number(partialFromApi),
    };
  }

  const items = Array.isArray(rentData.items) ? rentData.items : [];
  return {
    paid: items.filter((i) => String(i.paymentStatus).toUpperCase() === 'PAID').length,
    unpaid: items.filter((i) => String(i.paymentStatus).toUpperCase() === 'UNPAID').length,
    partial: items.filter((i) => String(i.paymentStatus).toUpperCase() === 'PARTIAL').length,
  };
}

function unitHasAssignedTenant(unit) {
  if (!unit) return false;
  const name = unit.tenantName != null ? String(unit.tenantName).trim() : '';
  const id = unit.tenantId != null ? String(unit.tenantId).trim() : '';
  return !!(name || id);
}

/** Listing is never public while a tenant is assigned (unit is not available to list). */
function unitListingIsPublic(unit) {
  if (unitHasAssignedTenant(unit)) return false;
  if (unit.isPublic !== undefined) return !!unit.isPublic;
  return unit.listingType === 'public';
}

function unitsArrayFromApiResponse(unitsResponse) {
  if (!unitsResponse || unitsResponse.status !== 'success' || !unitsResponse.data) return [];
  const d = unitsResponse.data;
  if (Array.isArray(d)) return d;
  if (d.units && Array.isArray(d.units)) return d.units;
  if (d.data && Array.isArray(d.data)) return d.data;
  return [];
}

/** Stable unit id for API calls, React keys, and list updates. */
function getUnitCanonicalId(unit) {
  if (!unit) return '';
  return String(unit.id ?? unit.unitId ?? unit.uuid ?? '').trim();
}

function unitMatchesId(unitOrId, targetId) {
  const target = String(targetId ?? '').trim();
  if (!target) return false;
  if (typeof unitOrId === 'string' || typeof unitOrId === 'number') {
    return String(unitOrId).trim() === target;
  }
  if (getUnitCanonicalId(unitOrId) === target) return true;
  const extras = [unitOrId?.uuid, unitOrId?.unitUUID].filter(Boolean).map((v) => String(v).trim());
  return extras.includes(target);
}

const PropertyDetails = ({
  propertyId,
  propertyScope = 'landlord',
  onBack,
  editModalOnly = false,
  onEditPropertyClose,
  onPropertyUpdated,
}) => {
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [authenticatedImages, setAuthenticatedImages] = useState({}); // { imageIndex: blobUrl }
  const [authenticatedAgreementUrl, setAuthenticatedAgreementUrl] = useState(null);
  /** True when property has agreementUrl but authenticated fetch returned no file (e.g. API 404). */
  const [agreementFileMissing, setAgreementFileMissing] = useState(false);
  /** Always latest property for image-loading effect (avoid re-running when only units etc. change). */
  const propertyRef = useRef(null);
  propertyRef.current = property;

  const [showAddUnit, setShowAddUnit] = useState(false);
  const [showLinkTenant, setShowLinkTenant] = useState(null);
  const [showUpdateUnit, setShowUpdateUnit] = useState(null);
  const [unitFilter, setUnitFilter] = useState('all');
  const [showBulkAddUnit, setShowBulkAddUnit] = useState(false);
  const [unitSearchQuery, setUnitSearchQuery] = useState('');
  const [selectedUnitForHistory, setSelectedUnitForHistory] = useState(null);
  const [selectedUnitHistoryLabel, setSelectedUnitHistoryLabel] = useState('');
  // BNB booking-based occupancy: Set of unit IDs that have an active booking covering today
  const [bnbOccupiedUnitIds, setBnbOccupiedUnitIds] = useState(new Set());
  const [rentStatusData, setRentStatusData] = useState(null);
  const [rentStatusLoading, setRentStatusLoading] = useState(false);
  const [showRentReport, setShowRentReport] = useState(false);

  // Tenant rating state
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedTenantForRating, setSelectedTenantForRating] = useState(null);
  const [ratingSuccessMessage, setRatingSuccessMessage] = useState('');

  // Bulk edit state
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [selectedUnitIds, setSelectedUnitIds] = useState([]);
  const [bulkEditForm, setBulkEditForm] = useState({
    unitType: '',
    unitNumberPrefix: '',
    unitNumberSuffix: '',
    rent: '',
    applyToAll: false
  });

  // Edit property modal state
  const [showEditProperty, setShowEditProperty] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateErrorMessage, setUpdateErrorMessage] = useState('');

  // Unit creation form state
  const [unitForm, setUnitForm] = useState({
    unitNumber: '',
    name: '',
    description: '',
    unitTypeDefinitionId: '',
    unitCategoryDefinitionId: '',
    unitSubCategoryId: '',
    bedrooms: 0,
    bathrooms: 0,
    sizeSqm: '',
    amenities: [],
    photoFiles: [],
    photoPreviews: [],
    monthlyRent: '',
    depositAmount: '',
    rentDueDay: '',
    pricePerNight: '',
    checkoutTime: '',
    isPublic: true
  });
  const [submittingUnit, setSubmittingUnit] = useState(false);
  const [unitErrorMessage, setUnitErrorMessage] = useState('');

  // Bulk unit creation form state
  const [bulkUnitForm, setBulkUnitForm] = useState({
    // Template fields
    name: '',
    description: '',
    unitTypeDefinitionId: '',
    unitCategoryDefinitionId: '',
    unitSubCategoryId: '',
    bedrooms: 0,
    bathrooms: 0,
    sizeSqm: '',
    amenities: [],
    photoFiles: [],
    photoPreviews: [],
    monthlyRent: '',
    depositAmount: '',
    rentDueDay: '',
    pricePerNight: '',
    checkoutTime: '',
    isPublic: true,
    // Unit numbers (comma-separated)
    unitNumbersInput: ''
  });
  const [submittingBulkUnits, setSubmittingBulkUnits] = useState(false);
  const [bulkUnitErrorMessage, setBulkUnitErrorMessage] = useState('');

  // Update unit form state
  const [updateUnitForm, setUpdateUnitForm] = useState({
    unitNumber: '',
    name: '',
    description: '',
    unitTypeDefinitionId: '',
    unitCategoryDefinitionId: '',
    unitSubCategoryId: '',
    photoFiles: [],
    photoPreviews: [],
    existingPhotos: [],
    monthlyRent: '',
    depositAmount: '',
    rentDueDay: '',
    pricePerNight: '',
    checkoutTime: '',
    isAvailable: true,
    isPublic: true
  });
  const [updatingUnit, setUpdatingUnit] = useState(false);
  const [updateUnitErrorMessage, setUpdateUnitErrorMessage] = useState('');
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [deletingUnit, setDeletingUnit] = useState(false);
  const [deleteUnitError, setDeleteUnitError] = useState('');
  const [selectedUnitForUpdate, setSelectedUnitForUpdate] = useState(null); // Store the unit being updated to check its type
  const [authenticatedUnitPhotos, setAuthenticatedUnitPhotos] = useState({}); // { photoIndex: blobUrl }

  // Unit types, categories, subcategories
  const [unitTypes, setUnitTypes] = useState([]);
  const [unitCategories, setUnitCategories] = useState([]);
  const [unitSubcategories, setUnitSubcategories] = useState([]);
  const [loadingUnitTypes, setLoadingUnitTypes] = useState(false);
  const [loadingUnitCategories, setLoadingUnitCategories] = useState(false);
  const [loadingUnitSubcategories, setLoadingUnitSubcategories] = useState(false);

  const availableAmenities = [
    'parking', 'balcony', 'gym', 'swimming_pool', 'security',
    'elevator', 'wifi', 'water', 'electricity', 'garden',
    'playground', 'cctv', 'generator', 'water_heater', 'air_conditioning'
  ];

  const fetchUnitTypes = async () => {
    try {
      setLoadingUnitTypes(true);
      const response = await getUnitTypes();
      let typesList = response.data || response || [];
      if (!Array.isArray(typesList)) {
        typesList = [];
      }
      setUnitTypes(typesList);
    } catch (error) {
      console.error('Error fetching unit types:', error);
      setUnitTypes([]);
    } finally {
      setLoadingUnitTypes(false);
    }
  };

  const fetchUnitCategories = async () => {
    try {
      setLoadingUnitCategories(true);
      const response = await getUnitCategories();
      let categoriesList = response.data || response || [];
      if (!Array.isArray(categoriesList)) {
        categoriesList = [];
      }
      setUnitCategories(categoriesList);
    } catch (error) {
      console.error('Error fetching unit categories:', error);
      setUnitCategories([]);
    } finally {
      setLoadingUnitCategories(false);
    }
  };

  const fetchUnitSubcategories = async (categoryId) => {
    try {
      setLoadingUnitSubcategories(true);
      const response = await getUnitSubcategories(categoryId);
      let subcategoriesList = response.data || response || [];
      if (!Array.isArray(subcategoriesList)) {
        subcategoriesList = [];
      }
      setUnitSubcategories(subcategoriesList);
    } catch (error) {
      console.error('Error fetching unit subcategories:', error);
      setUnitSubcategories([]);
    } finally {
      setLoadingUnitSubcategories(false);
    }
  };

  const handleUnitPhotosChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Limit to 6 images, minimum 2
    const currentCount = unitForm.photoFiles.length;
    const filesToAdd = files.slice(0, 6 - currentCount);
    if (filesToAdd.length === 0) {
      setUnitErrorMessage('Maximum 6 images allowed');
      return;
    }

    const previews = filesToAdd.map(file => URL.createObjectURL(file));

    setUnitForm((prev) => ({
      ...prev,
      photoFiles: [...prev.photoFiles, ...filesToAdd],
      photoPreviews: [...prev.photoPreviews, ...previews]
    }));
    setUnitErrorMessage('');
  };

  const handleRemoveUnitPhoto = (index) => {
    setUnitForm((prev) => {
      const newFiles = [...prev.photoFiles];
      const newPreviews = [...prev.photoPreviews];

      // Revoke object URL to free memory
      URL.revokeObjectURL(newPreviews[index]);

      newFiles.splice(index, 1);
      newPreviews.splice(index, 1);

      return {
        ...prev,
        photoFiles: newFiles,
        photoPreviews: newPreviews
      };
    });
  };

  const toggleUnitAmenity = (amenity) => {
    setUnitForm((prev) => {
      const amenities = prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity];
      return { ...prev, amenities };
    });
  };

  const handleBulkUnitPhotosChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const currentCount = bulkUnitForm.photoFiles.length;
    const filesToAdd = files.slice(0, 6 - currentCount);
    if (filesToAdd.length === 0) {
      setBulkUnitErrorMessage('Maximum 6 images allowed');
      return;
    }

    const previews = filesToAdd.map(file => URL.createObjectURL(file));

    setBulkUnitForm((prev) => ({
      ...prev,
      photoFiles: [...prev.photoFiles, ...filesToAdd],
      photoPreviews: [...prev.photoPreviews, ...previews]
    }));
    setBulkUnitErrorMessage('');
  };

  const handleRemoveBulkUnitPhoto = (index) => {
    setBulkUnitForm((prev) => {
      const newFiles = [...prev.photoFiles];
      const newPreviews = [...prev.photoPreviews];

      URL.revokeObjectURL(newPreviews[index]);

      newFiles.splice(index, 1);
      newPreviews.splice(index, 1);

      return {
        ...prev,
        photoFiles: newFiles,
        photoPreviews: newPreviews
      };
    });
  };

  const toggleBulkUnitAmenity = (amenity) => {
    setBulkUnitForm((prev) => {
      const amenities = prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity];
      return { ...prev, amenities };
    });
  };

  // Update unit photo handlers
  const handleUpdateUnitPhotosChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const currentCount = updateUnitForm.photoFiles.length;
    const existingCount = updateUnitForm.existingPhotos.length;
    const totalCount = currentCount + existingCount;
    const filesToAdd = files.slice(0, 6 - totalCount);

    if (filesToAdd.length === 0) {
      setUpdateUnitErrorMessage('Maximum 6 images allowed');
      return;
    }

    const previews = filesToAdd.map(file => URL.createObjectURL(file));

    setUpdateUnitForm((prev) => ({
      ...prev,
      photoFiles: [...prev.photoFiles, ...filesToAdd],
      photoPreviews: [...prev.photoPreviews, ...previews]
    }));
    setUpdateUnitErrorMessage('');
  };

  const handleRemoveUpdateUnitPhoto = (index, isExisting = false) => {
    setUpdateUnitForm((prev) => {
      if (isExisting) {
        const newExisting = [...prev.existingPhotos];
        newExisting.splice(index, 1);
        return {
          ...prev,
          existingPhotos: newExisting
        };
      } else {
        const newFiles = [...prev.photoFiles];
        const newPreviews = [...prev.photoPreviews];
        URL.revokeObjectURL(newPreviews[index]);
        newFiles.splice(index, 1);
        newPreviews.splice(index, 1);
        return {
          ...prev,
          photoFiles: newFiles,
          photoPreviews: newPreviews
        };
      }
    });
  };

  const paymentMethods = [
    { value: 'PAYBILL', label: 'Paybill' },
    { value: 'TILL', label: 'Till Number' },
    { value: 'POCHI', label: 'Pochi' },
    { value: 'SEND_MONEY', label: 'Send Money' },
    { value: 'BANK', label: 'Bank Transfer' },
    { value: 'CASH', label: 'Cash' },
    { value: 'MULTIPLE', label: 'Multiple Methods' }
  ];

  const [editPropertyForm, setEditPropertyForm] = useState({
    name: '',
    address: '',
    description: '',
    location: { lat: '', lng: '', city: '', area: '', locality: '', suburb: '' },
    amenities: [],
    imageUrls: [],
    imageFiles: [],
    imagePreviews: [],
    existingImages: [],
    contacts: {
      phone: '',
      email: '',
      caretakerPhone: ''
    },
    paymentDetails: {
      method: 'PAYBILL',
      paybillNumber: '',
      paybillAccount: '',
      accountName: '',
      acceptsPochi: false,
      acceptsSendMoney: false
    },
    agreementFile: null,
    agreementPreview: null,
    agreementUrl: '',
    unitCount: '',
    published: true,
    rentGraceDays: '',
    lateFeeType: 'FIXED_PER_DAY',
    lateFeeValue: '',
    waterIncludedInRent: false,
    waterRatePerUnit: ''
  });

  // Fetch unit types, categories, and subcategories when modal opens
  useEffect(() => {
    if (showAddUnit) {
      fetchUnitTypes();
      fetchUnitCategories();
    }
  }, [showAddUnit]);

  // Fetch subcategories when category changes (for single unit)
  useEffect(() => {
    if (showAddUnit && unitForm.unitCategoryDefinitionId) {
      fetchUnitSubcategories(unitForm.unitCategoryDefinitionId);
    } else {
      setUnitSubcategories([]);
    }
  }, [showAddUnit, unitForm.unitCategoryDefinitionId]);

  // Fetch unit types, categories when bulk modal opens
  useEffect(() => {
    if (showBulkAddUnit) {
      fetchUnitTypes();
      fetchUnitCategories();
    }
  }, [showBulkAddUnit]);

  // Fetch subcategories when bulk category changes
  useEffect(() => {
    if (showBulkAddUnit && bulkUnitForm.unitCategoryDefinitionId) {
      fetchUnitSubcategories(bulkUnitForm.unitCategoryDefinitionId);
    }
  }, [showBulkAddUnit, bulkUnitForm.unitCategoryDefinitionId]);

  // Fetch unit types, categories when update modal opens
  useEffect(() => {
    if (showUpdateUnit) {
      fetchUnitTypes();
      fetchUnitCategories();
    }
  }, [showUpdateUnit]);

  // Fetch subcategories when update category changes
  useEffect(() => {
    if (showUpdateUnit && updateUnitForm.unitCategoryDefinitionId) {
      fetchUnitSubcategories(updateUnitForm.unitCategoryDefinitionId);
    }
  }, [showUpdateUnit, updateUnitForm.unitCategoryDefinitionId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!showAddUnit) {
      setUnitForm({
        unitNumber: '',
        name: '',
        description: '',
        unitTypeDefinitionId: '',
        unitCategoryDefinitionId: '',
        unitSubCategoryId: '',
        bedrooms: 0,
        bathrooms: 0,
        sizeSqm: '',
        amenities: [],
        photoFiles: [],
        photoPreviews: [],
        monthlyRent: '',
        depositAmount: '',
        rentDueDay: '',
        pricePerNight: '',
        checkoutTime: '',
        isPublic: true
      });
      setUnitErrorMessage('');
    }
  }, [showAddUnit]);

  // Reset bulk form when modal closes
  useEffect(() => {
    if (!showBulkAddUnit) {
      setBulkUnitForm({
        name: '',
        description: '',
        unitTypeDefinitionId: '',
        unitCategoryDefinitionId: '',
        unitSubCategoryId: '',
        bedrooms: 0,
        bathrooms: 0,
        sizeSqm: '',
        amenities: [],
        photoFiles: [],
        photoPreviews: [],
        monthlyRent: '',
        depositAmount: '',
        rentDueDay: '',
        pricePerNight: '',
        checkoutTime: '',
        isPublic: true,
        unitNumbersInput: ''
      });
      setBulkUnitErrorMessage('');
    }
  }, [showBulkAddUnit]);

  // Reset update form when modal closes
  useEffect(() => {
    if (!showUpdateUnit) {
      setUpdateUnitForm({
        unitNumber: '',
        name: '',
        description: '',
        unitTypeDefinitionId: '',
        unitCategoryDefinitionId: '',
        unitSubCategoryId: '',
        photoFiles: [],
        photoPreviews: [],
        existingPhotos: [],
        monthlyRent: '',
        depositAmount: '',
        rentDueDay: '',
        pricePerNight: '',
        checkoutTime: '',
        isAvailable: true,
        isPublic: true
      });
      setSelectedUnitForUpdate(null);
      setUpdateUnitErrorMessage('');
      setAuthenticatedUnitPhotos({});
    }
  }, [showUpdateUnit]);

  const hydrateUnitsWithActiveTenancy = useCallback(
    (units = []) => hydrateUnitsWithTenancy(propertyId, units),
    [propertyId]
  );

  const fetchUnitsForProperty = useCallback(async (pid) => {
    try {
      const isBnb = String(propertyScope).toLowerCase() === 'bnb';
      const unitsResponse = await getPropertyUnits(pid, {
        dashboardScope: isBnb ? 'BNB' : 'RENTAL',
      });
      const raw = unitsArrayFromApiResponse(unitsResponse);
      return filterUnitsByListingScope(raw, isBnb ? 'BNB' : 'RENTAL');
    } catch (e) {
      console.error('Error fetching units:', e);
      return [];
    }
  }, [propertyScope]);

  const refreshPropertyUnits = useCallback(async () => {
    const currentPropertyId = property?.id || propertyId;
    if (!currentPropertyId) return [];

    const unitsArrayRaw = await fetchUnitsForProperty(currentPropertyId);
    const unitsArray = await hydrateUnitsWithActiveTenancy(unitsArrayRaw);

    setProperty((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        units: unitsArray,
        unitCount: unitsArray.length,
      };
    });

    return unitsArray;
  }, [property?.id, propertyId, fetchUnitsForProperty, hydrateUnitsWithActiveTenancy]);

  // Fetch property details from API
  useEffect(() => {
    const fetchProperty = async () => {
      if (!propertyId) return;

      setLoading(true);
      setErrorMessage('');

      try {
        const response = await getPropertyById(propertyId, true, {
          dashboardScope: String(propertyScope).toLowerCase() === 'bnb' ? 'BNB' : 'RENTAL',
        });
        console.log('Property response:', response);

        if (response.status === 'success' && response.data) {
          const propertyData = response.data;

          // Always load units from the units endpoint (property payload can include stale/deleted units)
          const units = await fetchUnitsForProperty(propertyId);
          propertyData.units = await hydrateUnitsWithActiveTenancy(units);
          propertyData.unitCount = propertyData.units.length;
          setProperty(propertyData);
        } else {
          setErrorMessage('Failed to load property details');
        }
      } catch (error) {
        console.error('Error fetching property:', error);
        setErrorMessage(error.message || 'Failed to load property details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [propertyId, hydrateUnitsWithActiveTenancy, propertyScope, fetchUnitsForProperty]);

  // For BNB scope: fetch bookings and determine which units are occupied today
  useEffect(() => {
    if (String(propertyScope).toLowerCase() !== 'bnb' || !propertyId) {
      setBnbOccupiedUnitIds(new Set());
      return;
    }
    let cancelled = false;

    const fetchBnbOccupancy = async () => {
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const ACTIVE_STATUSES = ['CONFIRMED', 'APPROVED', 'ASSIGNED', 'CHECKED_IN'];
        const res = await getManageBnbBookings(propertyId);
        const bookings = Array.isArray(res?.data) ? res.data : [];

        const occupiedIds = new Set();
        bookings.forEach((b) => {
          const status = String(b?.status || '').toUpperCase();
          if (!ACTIVE_STATUSES.includes(status)) return;

          const checkIn = new Date(b.checkInDate);
          const checkOut = new Date(b.checkOutDate);
          checkIn.setHours(0, 0, 0, 0);
          checkOut.setHours(0, 0, 0, 0);

          if (isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) return;

          if (today >= checkIn && today < checkOut) {
            const unitId = b.unitId || b.unitID || b.unit?.id || b.propertyUnitId;
            if (unitId) occupiedIds.add(String(unitId));
          }
        });

        if (!cancelled) setBnbOccupiedUnitIds(occupiedIds);
      } catch (_) {
        if (!cancelled) setBnbOccupiedUnitIds(new Set());
      }
    };

    fetchBnbOccupancy();
    return () => { cancelled = true; };
  }, [propertyId, propertyScope]);

  // Fetch current-month rent status for rental properties (paid / unpaid / partial counts)
  useEffect(() => {
    const currentPropertyId = property?.id || propertyId;
    if (!currentPropertyId || String(propertyScope).toLowerCase() === 'bnb') {
      setRentStatusData(null);
      return;
    }

    let cancelled = false;
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;

    const loadRentStatus = async () => {
      setRentStatusLoading(true);
      try {
        const res = await getRentStatus(currentPropertyId, year, month);
        if (!cancelled) setRentStatusData(res?.data || null);
      } catch (_) {
        if (!cancelled) setRentStatusData(null);
      } finally {
        if (!cancelled) setRentStatusLoading(false);
      }
    };

    loadRentStatus();
    return () => { cancelled = true; };
  }, [property?.id, propertyId, propertyScope]);

  const propertyMediaKey = useMemo(() => {
    if (!property?.id) return '';
    const urls =
      property.imageUrls?.length > 0
        ? property.imageUrls
        : property.imageUrl
          ? [property.imageUrl]
          : [];
    return JSON.stringify({
      id: property.id,
      urls,
      agreement: property.agreementUrl || '',
    });
  }, [property?.id, property?.imageUrls, property?.imageUrl, property?.agreementUrl]);

  // Fetch authenticated images for property (blob URLs from fetchAuthenticatedImage are global until logout — never revoke here)
  useEffect(() => {
    if (!propertyMediaKey) return;

    let cancelled = false;

    const loadAuthenticatedImages = async () => {
      const p = propertyRef.current;
      if (!p) return;

      const newAuthenticatedImages = {};

      const propertyImages =
        p.imageUrls && p.imageUrls.length > 0
          ? p.imageUrls
          : p.imageUrl
            ? [p.imageUrl]
            : [];

      for (let imgIndex = 0; imgIndex < propertyImages.length; imgIndex++) {
        const imageUrl = propertyImages[imgIndex];
        const imageKey = `img_${imgIndex}`;

        if (imageUrl && (imageUrl.startsWith('blob:') || imageUrl.startsWith('data:'))) {
          newAuthenticatedImages[imageKey] = imageUrl;
          continue;
        }

        if (!imageUrl || !imageUrl.startsWith('http')) {
          continue;
        }

        try {
          const cachedImage = getImageFromCache(imageUrl);
          if (cachedImage) {
            newAuthenticatedImages[imageKey] = cachedImage;
          } else {
            const blobUrl = await fetchAuthenticatedImage(imageUrl);
            if (blobUrl) {
              newAuthenticatedImages[imageKey] = blobUrl;
              await storeImageInCache(imageUrl, blobUrl);
            }
          }
        } catch (error) {
          console.error(`Error loading image ${imgIndex}:`, error);
        }
      }

      if (cancelled) return;

      if (p.agreementUrl && p.agreementUrl.startsWith('http')) {
        try {
          const agreementBlobUrl = await fetchAuthenticatedImage(p.agreementUrl);
          if (cancelled) return;
          if (agreementBlobUrl) {
            setAuthenticatedAgreementUrl(agreementBlobUrl);
            setAgreementFileMissing(false);
          } else {
            setAuthenticatedAgreementUrl(null);
            setAgreementFileMissing(true);
          }
        } catch (error) {
          if (cancelled) return;
          console.error('Error loading agreement document:', error);
          setAuthenticatedAgreementUrl(null);
          setAgreementFileMissing(true);
        }
      } else if (!cancelled) {
        setAuthenticatedAgreementUrl(null);
        setAgreementFileMissing(false);
      }

      if (!cancelled) {
        setAuthenticatedImages(newAuthenticatedImages);
      }
    };

    loadAuthenticatedImages();
    return () => {
      cancelled = true;
    };
  }, [propertyMediaKey]);

  // Initialize edit form when opening modal
  useEffect(() => {
    if (showEditProperty && property) {
      const propertyImages = property.imageUrls && property.imageUrls.length > 0
        ? property.imageUrls
        : (property.imageUrl ? [property.imageUrl] : []);

      // Initialize payment details based on method
      let paymentDetails = {
        method: property.paymentDetails?.method || 'PAYBILL',
        accountName: property.paymentDetails?.accountName || ''
      };
      paymentDetails.acceptsPochi = !!property.paymentDetails?.acceptsPochi;
      paymentDetails.acceptsSendMoney = !!property.paymentDetails?.acceptsSendMoney;

      if (property.paymentDetails?.method === 'PAYBILL') {
        paymentDetails = {
          ...paymentDetails,
          paybillNumber: property.paymentDetails.paybillNumber || '',
          paybillAccount: property.paymentDetails.paybillAccount || ''
        };
      } else if (property.paymentDetails?.method === 'TILL') {
        paymentDetails = {
          ...paymentDetails,
          tillNumber: property.paymentDetails.tillNumber || ''
        };
      } else if (property.paymentDetails?.method === 'POCHI') {
        paymentDetails = {
          ...paymentDetails,
          pochiNumber: property.paymentDetails.pochiNumber || ''
        };
      } else if (property.paymentDetails?.method === 'SEND_MONEY') {
        paymentDetails = {
          ...paymentDetails,
          phoneNumber: property.paymentDetails.phoneNumber || ''
        };
      } else if (property.paymentDetails?.method === 'BANK') {
        paymentDetails = {
          ...paymentDetails,
          bankName: property.paymentDetails.bankName || '',
          accountNumber: property.paymentDetails.accountNumber || '',
          branch: property.paymentDetails.branch || '',
          swiftCode: property.paymentDetails.swiftCode || ''
        };
      } else if (property.paymentDetails?.method === 'CASH') {
        paymentDetails = {
          ...paymentDetails,
          instructions: property.paymentDetails.instructions || ''
        };
      }

      setEditPropertyForm({
        name: property.name || '',
        address: property.address || '',
        description: property.description || '',
        location: {
          lat: property.location?.lat?.toString() || '',
          lng: property.location?.lng?.toString() || '',
          city: property.location?.city || '',
          area: property.location?.area || '',
          locality: property.location?.locality || '',
          suburb: property.location?.suburb || ''
        },
        amenities: property.amenities || [],
        imageUrls: propertyImages,
        imageFiles: [],
        imagePreviews: [],
        existingImages: propertyImages,
        contacts: {
          phone: property.contacts?.phone || '',
          email: property.contacts?.email || '',
          caretakerPhone: property.contacts?.caretakerPhone || ''
        },
        paymentDetails: paymentDetails,
        agreementFile: null,
        agreementPreview: null,
        agreementUrl: property.agreementUrl || '',
        unitCount: property.unitCount?.toString() || '',
        published: property.published !== undefined ? !!property.published : true,
        rentGraceDays: property.rentGraceDays !== undefined && property.rentGraceDays !== null ? property.rentGraceDays.toString() : '',
        lateFeeType: property.lateFeeType || 'FIXED_PER_DAY',
        lateFeeValue: property.lateFeeValue !== undefined && property.lateFeeValue !== null ? property.lateFeeValue.toString() : '',
        waterIncludedInRent: property.waterIncludedInRent === true,
        waterRatePerUnit:
          property.waterRatePerUnit !== undefined && property.waterRatePerUnit !== null
            ? property.waterRatePerUnit.toString()
            : ''
      });
    }
  }, [showEditProperty, property]);

  useEffect(() => {
    if (editModalOnly && property && !loading) {
      setShowEditProperty(true);
    }
  }, [editModalOnly, property, loading]);

  useEffect(() => {
    if (editModalOnly && !loading && !property && onEditPropertyClose) {
      onEditPropertyClose();
    }
  }, [editModalOnly, loading, property, onEditPropertyClose]);

  const closeEditPropertyModal = () => {
    if (updating) return;
    setShowEditProperty(false);
    if (editModalOnly) {
      onEditPropertyClose?.();
    }
  };

  // Handle edit property images change
  const handleEditPropertyImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const currentTotal = editPropertyForm.existingImages.length + editPropertyForm.imageFiles.length;
    const filesToAdd = files.slice(0, 6 - currentTotal);
    if (filesToAdd.length === 0) {
      setUpdateErrorMessage('Maximum 6 images allowed');
      return;
    }

    const previews = filesToAdd.map(file => URL.createObjectURL(file));

    setEditPropertyForm((prev) => ({
      ...prev,
      imageFiles: [...prev.imageFiles, ...filesToAdd],
      imagePreviews: [...prev.imagePreviews, ...previews]
    }));
  };

  // Handle remove image from edit form
  const handleRemoveEditImage = (index, isExisting = false) => {
    setEditPropertyForm((prev) => {
      if (isExisting) {
        const newExisting = [...prev.existingImages];
        newExisting.splice(index, 1);
        return {
          ...prev,
          existingImages: newExisting,
          imageUrls: newExisting
        };
      } else {
        const newFiles = [...prev.imageFiles];
        const newPreviews = [...prev.imagePreviews];
        URL.revokeObjectURL(newPreviews[index]);
        newFiles.splice(index, 1);
        newPreviews.splice(index, 1);
        return {
          ...prev,
          imageFiles: newFiles,
          imagePreviews: newPreviews
        };
      }
    });
  };

  // Handle agreement change
  const handleEditAgreementChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setEditPropertyForm((prev) => ({
      ...prev,
      agreementFile: file,
      agreementPreview: previewUrl
    }));
  };

  // Toggle amenity
  const toggleEditAmenity = (amenity) => {
    setEditPropertyForm((prev) => {
      const amenities = prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity];
      return { ...prev, amenities };
    });
  };

  // Handle edit property submit
  const handleEditPropertySubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setUpdateErrorMessage('');

    try {
      // Upload new images first
      const newImageUrls = [];
      for (let i = 0; i < editPropertyForm.imageFiles.length; i++) {
        const file = editPropertyForm.imageFiles[i];
        const previewUrl = editPropertyForm.imagePreviews[i];

        const uploadResponse = await uploadPropertyImage(file, propertyId);
        if (uploadResponse.status === 'success' && uploadResponse.data?.url) {
          const imageUrl = uploadResponse.data.url;
          newImageUrls.push(imageUrl);

          // Store uploaded image in cache
          if (previewUrl) {
            await storeImageInCache(imageUrl, previewUrl);
          }
        } else {
          throw new Error('Failed to upload one or more images');
        }
      }

      // Combine existing and new images
      const allImageUrls = [...editPropertyForm.existingImages, ...newImageUrls];

      if (allImageUrls.length === 0) {
        throw new Error('At least one property image is required');
      }

      // Upload agreement document if provided
      let agreementUrl = editPropertyForm.agreementUrl;
      if (editPropertyForm.agreementFile) {
        const agreementResponse = await uploadAgreementDocument(editPropertyForm.agreementFile, propertyId);
        if (agreementResponse.status === 'success' && agreementResponse.data?.url) {
          agreementUrl = agreementResponse.data.url;
        } else {
          throw new Error('Failed to upload agreement document');
        }
      }

      // Prepare payment details based on method
      let paymentDetails = {};
      const method = editPropertyForm.paymentDetails.method;
      const acceptsPochi = !!editPropertyForm.paymentDetails.acceptsPochi;
      const acceptsSendMoney = !!editPropertyForm.paymentDetails.acceptsSendMoney;

      if (method === 'PAYBILL') {
        paymentDetails = {
          method: 'PAYBILL',
          paybillNumber: editPropertyForm.paymentDetails.paybillNumber,
          paybillAccount: editPropertyForm.paymentDetails.paybillAccount,
          accountName: editPropertyForm.paymentDetails.accountName,
          acceptsPochi,
          acceptsSendMoney
        };
      } else if (method === 'TILL') {
        paymentDetails = {
          method: 'TILL',
          tillNumber: editPropertyForm.paymentDetails.tillNumber,
          accountName: editPropertyForm.paymentDetails.accountName,
          acceptsPochi,
          acceptsSendMoney
        };
      } else if (method === 'POCHI') {
        paymentDetails = {
          method: 'POCHI',
          pochiNumber: editPropertyForm.paymentDetails.pochiNumber,
          accountName: editPropertyForm.paymentDetails.accountName,
          acceptsPochi,
          acceptsSendMoney
        };
      } else if (method === 'SEND_MONEY') {
        paymentDetails = {
          method: 'SEND_MONEY',
          phoneNumber: editPropertyForm.paymentDetails.phoneNumber,
          accountName: editPropertyForm.paymentDetails.accountName,
          acceptsPochi,
          acceptsSendMoney
        };
      } else if (method === 'BANK') {
        paymentDetails = {
          method: 'BANK',
          bankName: editPropertyForm.paymentDetails.bankName,
          accountName: editPropertyForm.paymentDetails.accountName,
          accountNumber: editPropertyForm.paymentDetails.accountNumber,
          branch: editPropertyForm.paymentDetails.branch,
          swiftCode: editPropertyForm.paymentDetails.swiftCode,
          acceptsPochi,
          acceptsSendMoney
        };
      } else if (method === 'CASH') {
        paymentDetails = {
          method: 'CASH',
          instructions: editPropertyForm.paymentDetails.instructions || 'Pay at caretaker office',
          acceptsPochi,
          acceptsSendMoney
        };
      } else if (method === 'MULTIPLE') {
        paymentDetails = {
          method: 'MULTIPLE',
          options: editPropertyForm.paymentDetails.options || [],
          acceptsPochi,
          acceptsSendMoney
        };
      }

      // Prepare property update data (all fields optional; avoid overwriting with empty strings/zeros)
      const propertyData = {};

      if (editPropertyForm.name && editPropertyForm.name.trim()) propertyData.name = editPropertyForm.name.trim();
      if (editPropertyForm.address && editPropertyForm.address.trim()) propertyData.address = editPropertyForm.address.trim();
      if (editPropertyForm.description !== undefined) propertyData.description = editPropertyForm.description || '';

      // Location: include only if any part is provided (don't default lat/lng to 0)
      const loc = editPropertyForm.location || {};
      const hasAnyLocationField =
        (loc.lat !== '' && loc.lat !== undefined && loc.lat !== null) ||
        (loc.lng !== '' && loc.lng !== undefined && loc.lng !== null) ||
        (loc.city && loc.city.trim()) ||
        (loc.area && loc.area.trim()) ||
        (loc.locality && loc.locality.trim()) ||
        (loc.suburb && loc.suburb.trim());
      if (hasAnyLocationField) {
        const parsedLat = loc.lat === '' || loc.lat === undefined || loc.lat === null ? undefined : parseFloat(loc.lat);
        const parsedLng = loc.lng === '' || loc.lng === undefined || loc.lng === null ? undefined : parseFloat(loc.lng);
        propertyData.location = {
          ...(Number.isFinite(parsedLat) ? { lat: parsedLat } : {}),
          ...(Number.isFinite(parsedLng) ? { lng: parsedLng } : {}),
          ...(loc.city && loc.city.trim() ? { city: loc.city.trim() } : {}),
          ...(loc.area && loc.area.trim() ? { area: loc.area.trim() } : {}),
          ...(loc.locality && loc.locality.trim() ? { locality: loc.locality.trim() } : {}),
          ...(loc.suburb && loc.suburb.trim() ? { suburb: loc.suburb.trim() } : {}),
        };
      }

      if (Array.isArray(editPropertyForm.amenities)) propertyData.amenities = editPropertyForm.amenities;
      if (Array.isArray(allImageUrls) && allImageUrls.length > 0) propertyData.imageUrls = allImageUrls;

      const contacts = editPropertyForm.contacts || {};
      const hasAnyContact =
        (contacts.phone && contacts.phone.trim()) ||
        (contacts.email && contacts.email.trim()) ||
        (contacts.caretakerPhone && contacts.caretakerPhone.trim());
      if (hasAnyContact) {
        propertyData.contacts = {
          ...(contacts.phone && contacts.phone.trim() ? { phone: contacts.phone.trim() } : {}),
          ...(contacts.email && contacts.email.trim() ? { email: contacts.email.trim() } : {}),
          ...(contacts.caretakerPhone && contacts.caretakerPhone.trim() ? { caretakerPhone: contacts.caretakerPhone.trim() } : {}),
        };
      }

      if (paymentDetails && paymentDetails.method) {
        // Keep paymentDetails optional; only send if a method is selected.
        propertyData.paymentDetails = paymentDetails;
      }

      // Allow changing unitCount only if user provided one
      const parsedUnitCount = editPropertyForm.unitCount === '' ? null : parseInt(editPropertyForm.unitCount);
      if (Number.isFinite(parsedUnitCount) && parsedUnitCount !== null) propertyData.unitCount = parsedUnitCount;

      // published toggle is explicit; always include
      propertyData.published = !!editPropertyForm.published;

      // Agreement URL: include if we uploaded a new file OR existing value is a non-empty string
      if (typeof agreementUrl === 'string' && agreementUrl.trim()) {
        propertyData.agreementUrl = agreementUrl.trim();
      }

      const rentGraceDays = editPropertyForm.rentGraceDays === '' ? null : parseInt(editPropertyForm.rentGraceDays);
      const lateFeeValue = editPropertyForm.lateFeeValue === '' ? null : parseFloat(editPropertyForm.lateFeeValue);
      if (Number.isFinite(rentGraceDays) && rentGraceDays !== null) propertyData.rentGraceDays = rentGraceDays;
      if (editPropertyForm.lateFeeType && Number.isFinite(lateFeeValue) && lateFeeValue !== null) {
        propertyData.lateFeeType = editPropertyForm.lateFeeType;
        propertyData.lateFeeValue = lateFeeValue;
      }

      propertyData.waterIncludedInRent = !!editPropertyForm.waterIncludedInRent;
      if (!editPropertyForm.waterIncludedInRent) {
        const waterRate = editPropertyForm.waterRatePerUnit === '' ? null : parseFloat(editPropertyForm.waterRatePerUnit);
        if (Number.isFinite(waterRate)) propertyData.waterRatePerUnit = waterRate;
      }

      // Update property
      const response = await updateProperty(propertyId, propertyData);

      if (response.status === 'success') {
        // Refresh property data
        const refreshResponse = await getPropertyById(propertyId, true, {
          dashboardScope: String(propertyScope).toLowerCase() === 'bnb' ? 'BNB' : 'RENTAL',
        });
        console.log('Property refresh response after edit:', refreshResponse);
        if (refreshResponse.status === 'success' && refreshResponse.data) {
          const propertyData = refreshResponse.data;

          const units = await fetchUnitsForProperty(propertyId);
          propertyData.units = await hydrateUnitsWithActiveTenancy(units);
          propertyData.unitCount = propertyData.units.length;
          setProperty(propertyData);
        }
        setShowEditProperty(false);
        if (editModalOnly) {
          onPropertyUpdated?.(propertyId);
          onEditPropertyClose?.();
        }
      } else {
        throw new Error(response.message || 'Failed to update property');
      }
    } catch (error) {
      console.error('Error updating property:', error);
      setUpdateErrorMessage(error.message || 'Failed to update property. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const [tenantForm, setTenantForm] = useState({
    tenantId: '',
    tenantName: ''
  });
  const [linkingTenant, setLinkingTenant] = useState(false);
  const [linkTenantError, setLinkTenantError] = useState('');
  const [evictionModal, setEvictionModal] = useState({
    open: false,
    unitId: '',
    unitLabel: '',
    tenantName: '',
    assignmentId: '',
    moveOutDate: '',
    reason: '',
  });
  const [submittingEvictionId, setSubmittingEvictionId] = useState('');
  const [evictionActionError, setEvictionActionError] = useState('');
  const [evictionActionMessage, setEvictionActionMessage] = useState('');

  const [releaseModal, setReleaseModal] = useState({
    open: false,
    unitId: '',
    unitLabel: '',
    tenantName: '',
    assignmentId: '',
    releaseDate: '',
  });
  const [submittingReleaseId, setSubmittingReleaseId] = useState('');

  const handleOpenUnitHistory = (unitId, unitLabel = '') => {
    window.history.pushState(
      { [UNIT_HISTORY_HISTORY_KEY]: true, propertyId, unitId },
      '',
      window.location.href
    );
    setSelectedUnitForHistory(unitId);
    setSelectedUnitHistoryLabel(String(unitLabel || '').trim());
  };

  const closeEvictionModal = () => {
    if (submittingEvictionId) return;
    setEvictionModal({
      open: false,
      unitId: '',
      unitLabel: '',
      tenantName: '',
      assignmentId: '',
      moveOutDate: '',
      reason: '',
    });
  };

  const openEvictionModal = async (unit) => {
    const unitId = String(unit?.id || '').trim();
    if (!unitId) {
      setEvictionActionError('Missing unit ID for eviction.');
      return;
    }

    const now = new Date();
    setEvictionActionError('');
    setEvictionActionMessage('');
    setEvictionModal({
      open: true,
      unitId,
      unitLabel: unit?.unitNumber || unit?.name || unitId,
      tenantName: unit?.tenantName || 'Tenant',
      assignmentId: '',
      moveOutDate: now.toISOString().substring(0, 10),
      reason: '',
    });

    try {
      const historyResponse = await getPropertyUnitTenancyHistory(propertyId, unitId);
      const rows = Array.isArray(historyResponse?.data) ? historyResponse.data : [];
      const activeRows = rows.filter((row) => String(row?.status || '').toUpperCase() === 'ACTIVE');
      const latestActiveRow = [...activeRows].sort((a, b) => {
        const aTs = new Date(a?.createdAt || a?.startDate || 0).getTime();
        const bTs = new Date(b?.createdAt || b?.startDate || 0).getTime();
        return bTs - aTs;
      })[0];

      if (!latestActiveRow) {
        setEvictionActionError('No active tenant assignment found for this unit.');
        closeEvictionModal();
        return;
      }

      const assignmentId = String(latestActiveRow?.assignmentId || latestActiveRow?.id || '').trim();
      const tenantName = `${latestActiveRow?.userFirstName || ''} ${latestActiveRow?.userLastName || ''}`.trim()
        || latestActiveRow?.userEmail
        || unit?.tenantName
        || 'Tenant';

      setEvictionModal((prev) => ({
        ...prev,
        assignmentId,
        tenantName,
      }));
    } catch (e) {
      setEvictionActionError(e?.message || 'Failed to load tenant assignment for eviction.');
      closeEvictionModal();
    }
  };

  const openRatingModal = async (unit) => {
    const unitId = String(unit?.id || '').trim();
    if (!unitId) return;

    // Set initial data from unit
    setSelectedTenantForRating(unit);
    setShowRatingModal(true);

    try {
      // Fetch history to get the real userId (UUID)
      const historyResponse = await getPropertyUnitTenancyHistory(propertyId, unitId);
      const rows = Array.isArray(historyResponse?.data) ? historyResponse.data : [];
      
      // Find the active assignment row
      const activeRow = rows.find((row) => String(row?.status || '').toUpperCase() === 'ACTIVE');
      
      if (activeRow && activeRow.userId) {
        setSelectedTenantForRating(prev => ({
          ...prev,
          tenantUserId: activeRow.userId,
          // Also update name if we have it from history
          tenantName: `${activeRow.userFirstName || ''} ${activeRow.userLastName || ''}`.trim() || prev.tenantName
        }));
      }
    } catch (e) {
      console.error('Failed to load tenant info for rating:', e);
    }
  };

  const submitEvictionNotice = async () => {
    const assignmentId = String(evictionModal.assignmentId || '').trim();
    if (!assignmentId) {
      setEvictionActionError('Missing assignment ID for this tenant.');
      return;
    }

    const moveOutDate = evictionModal.moveOutDate;
    if (!moveOutDate) {
      setEvictionActionError('Move-out date is required.');
      return;
    }
    const reason = String(evictionModal.reason || '').trim();
    if (!reason) {
      setEvictionActionError('Reason is required.');
      return;
    }

    try {
      setSubmittingEvictionId(assignmentId);
      setEvictionActionError('');
      const res = await issueEvictionNotice(assignmentId, { moveOutDate, reason });
      const msg = res?.message || `Eviction notice issued for ${evictionModal.tenantName}.`;
      setEvictionActionMessage(msg);
      closeEvictionModal();
    } catch (e) {
      setEvictionActionError(e?.message || 'Failed to issue eviction notice.');
    } finally {
      setSubmittingEvictionId('');
    }
  };

  const closeReleaseModal = () => {
    if (submittingReleaseId) return;
    setReleaseModal({
      open: false,
      unitId: '',
      unitLabel: '',
      tenantName: '',
      assignmentId: '',
      releaseDate: '',
    });
  };

  const openReleaseModal = async (unit) => {
    const unitId = String(unit?.id || '').trim();
    if (!unitId) {
      setEvictionActionError('Missing unit ID for release.');
      return;
    }

    const now = new Date();
    setEvictionActionError('');
    setEvictionActionMessage('');
    setReleaseModal({
      open: true,
      unitId,
      unitLabel: unit?.unitNumber || unit?.name || unitId,
      tenantName: unit?.tenantName || 'Tenant',
      assignmentId: '',
      releaseDate: now.toISOString().substring(0, 10),
    });

    try {
      const historyResponse = await getPropertyUnitTenancyHistory(propertyId, unitId);
      const rows = Array.isArray(historyResponse?.data) ? historyResponse.data : [];
      const activeRows = rows.filter((row) => String(row?.status || '').toUpperCase() === 'ACTIVE');
      const latestActiveRow = [...activeRows].sort((a, b) => {
        const aTs = new Date(a?.createdAt || a?.startDate || 0).getTime();
        const bTs = new Date(b?.createdAt || b?.startDate || 0).getTime();
        return bTs - aTs;
      })[0];

      if (!latestActiveRow) {
        setEvictionActionError('No active tenant assignment found for this unit.');
        closeReleaseModal();
        return;
      }

      const assignmentId = String(latestActiveRow?.assignmentId || latestActiveRow?.id || '').trim();
      const tenantName = `${latestActiveRow?.userFirstName || ''} ${latestActiveRow?.userLastName || ''}`.trim()
        || latestActiveRow?.userEmail
        || unit?.tenantName
        || 'Tenant';

      setReleaseModal((prev) => ({
        ...prev,
        assignmentId,
        tenantName,
      }));
    } catch (e) {
      setEvictionActionError(e?.message || 'Failed to load tenant assignment for release.');
      closeReleaseModal();
    }
  };

  const submitReleaseTenant = async () => {
    const assignmentId = String(releaseModal.assignmentId || '').trim();
    if (!assignmentId) {
      setEvictionActionError('Missing assignment ID to release tenant.');
      return;
    }

    try {
      setSubmittingReleaseId(assignmentId);
      setEvictionActionError('');
      const res = await releaseTenant(assignmentId, releaseModal.releaseDate);
      const msg = res?.message || `Tenant ${releaseModal.tenantName} released successfully.`;
      setEvictionActionMessage(msg);

      // Update local state without full reload
      setProperty(prev => ({
        ...prev,
        units: (prev?.units || []).map(u =>
          u.id === releaseModal.unitId
            ? { ...u, tenantName: '', tenantId: null, status: 'VACANT', assignedUser: null }
            : u
        )
      }));

      closeReleaseModal();
    } catch (e) {
      setEvictionActionError(e?.message || 'Failed to release tenant.');
    } finally {
      setSubmittingReleaseId('');
    }
  };

  useEffect(() => {
    if (!selectedUnitForHistory) return undefined;
    const onPopState = () => {
      setSelectedUnitForHistory(null);
      setSelectedUnitHistoryLabel('');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [selectedUnitForHistory]);

  const handleSelectUnit = (unitId) => {
    setSelectedUnitIds((prev) =>
      prev.some((id) => unitMatchesId(id, unitId))
        ? prev.filter((id) => !unitMatchesId(id, unitId))
        : [...prev, unitId]
    );
  };

  // Get selected subcategory to determine if it's Rental or BNB (for JSX rendering)
  const selectedSubcategory = unitSubcategories.find(sub => sub.id === unitForm.unitSubCategoryId);
  const isRental = selectedSubcategory?.name?.toLowerCase() === 'rental';
  const isBNB = selectedSubcategory?.name?.toLowerCase() === 'bnb' || selectedSubcategory?.name?.toLowerCase() === 'airbnb';

  // Get selected subcategory for bulk form
  const bulkSelectedSubcategory = unitSubcategories.find(sub => sub.id === bulkUnitForm.unitSubCategoryId);
  const bulkIsRental = bulkSelectedSubcategory?.name?.toLowerCase() === 'rental';
  const bulkIsBNB = bulkSelectedSubcategory?.name?.toLowerCase() === 'bnb' || bulkSelectedSubcategory?.name?.toLowerCase() === 'airbnb';

  // If a unit is selected for history view, show UnitHistory component
  if (selectedUnitForHistory) {
    return (
      <UnitHistory
        propertyId={propertyId}
        unitId={selectedUnitForHistory}
        unitLabel={selectedUnitHistoryLabel}
        onBack={() => {
          setSelectedUnitForHistory(null);
          setSelectedUnitHistoryLabel('');
        }}
      />
    );
  }

  if (showRentReport) {
    return (
      <RentStatusDetails
        propertyId={property?.id || propertyId}
        onBack={() => setShowRentReport(false)}
      />
    );
  }

  if (loading && editModalOnly) {
    return createPortal(
      <div className="modal-overlay">
        <div className="modal-content" style={{ padding: '32px', textAlign: 'center' }}>
          <p>Loading property...</p>
        </div>
      </div>,
      document.body
    );
  }

  if (loading) {
    return (
      <div className="property-details-container">
        <div className="empty-state">Loading property details...</div>
      </div>
    );
  }

  if (!property && editModalOnly) {
    return null;
  }

  if (!property) {
    return (
      <div className="property-details-container">
        {errorMessage ? (
          <div className="error-message" style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#fee',
            color: '#c33',
            borderRadius: '4px'
          }}>
            {errorMessage}
          </div>
        ) : (
          <div className="error-message">Property not found</div>
        )}
      </div>
    );
  }

  const handleUnitSubmit = async (e) => {
    e.preventDefault();

    // Ensure we're using the correct propertyId - use property.id as source of truth
    const currentPropertyId = property?.id || propertyId;
    if (!currentPropertyId) {
      setUnitErrorMessage('Property ID is missing. Please refresh the page.');
      return;
    }

    // Log for debugging
    console.log('handleUnitSubmit - propertyId prop:', propertyId);
    console.log('handleUnitSubmit - property.id:', property?.id);
    console.log('handleUnitSubmit - using propertyId:', currentPropertyId);

    setSubmittingUnit(true);
    setUnitErrorMessage('');

    try {
      // Validate required fields
      if (!unitForm.unitNumber || !unitForm.name) {
        throw new Error('Unit number and name are required');
      }

      if (!unitForm.unitTypeDefinitionId || !unitForm.unitCategoryDefinitionId || !unitForm.unitSubCategoryId) {
        throw new Error('Unit type, category, and subcategory are required');
      }

      // Validate photos (2-6 required)
      if (unitForm.photoFiles.length < 2) {
        throw new Error('At least 2 photos are required (maximum 6)');
      }

      if (unitForm.photoFiles.length > 6) {
        throw new Error('Maximum 6 photos allowed');
      }

      // Validate based on subcategory
      const selectedSubcategory = unitSubcategories.find(sub => sub.id === unitForm.unitSubCategoryId);
      const isRental = selectedSubcategory?.name?.toLowerCase() === 'rental';
      const isBNB = selectedSubcategory?.name?.toLowerCase() === 'bnb' || selectedSubcategory?.name?.toLowerCase() === 'airbnb';

      if (isRental) {
        if (!unitForm.monthlyRent || parseFloat(unitForm.monthlyRent) <= 0) {
          throw new Error('Monthly rent is required for rental units');
        }
        if (!unitForm.depositAmount || parseFloat(unitForm.depositAmount) <= 0) {
          throw new Error('Deposit amount is required for rental units');
        }
      }

      if (isBNB) {
        if (!unitForm.pricePerNight || parseFloat(unitForm.pricePerNight) <= 0) {
          throw new Error('Price per night is required for BNB units');
        }
        if (!unitForm.checkoutTime) {
          throw new Error('Checkout time is required for BNB units (e.g., 11:00)');
        }
      }

      // Upload photos first
      const photoUrls = [];
      for (let i = 0; i < unitForm.photoFiles.length; i++) {
        const file = unitForm.photoFiles[i];
        const uploadResponse = await uploadUnitImage(file);
        if (uploadResponse.status === 'success' && uploadResponse.data?.url) {
          photoUrls.push(uploadResponse.data.url);
        } else {
          throw new Error('Failed to upload one or more photos');
        }
      }

      // Prepare unit data
      const unitData = {
        unitNumber: unitForm.unitNumber,
        name: unitForm.name,
        description: unitForm.description || '',
        unitTypeDefinitionId: unitForm.unitTypeDefinitionId,
        unitCategoryDefinitionId: unitForm.unitCategoryDefinitionId,
        unitSubCategoryId: unitForm.unitSubCategoryId,
        bedrooms: parseInt(unitForm.bedrooms) || 0,
        bathrooms: parseInt(unitForm.bathrooms) || 0,
        sizeSqm: parseFloat(unitForm.sizeSqm) || 0,
        amenities: unitForm.amenities,
        photos: photoUrls,
        isPublic: !!unitForm.isPublic
      };

      const rentDueDay = unitForm.rentDueDay === '' ? null : parseInt(unitForm.rentDueDay);
      if (Number.isFinite(rentDueDay) && rentDueDay !== null) {
        if (rentDueDay < 1 || rentDueDay > 28) {
          throw new Error('Rent due day must be between 1 and 28');
        }
        unitData.rentDueDay = rentDueDay;
      }

      // Add rental-specific fields
      if (isRental) {
        unitData.monthlyRent = parseFloat(unitForm.monthlyRent);
        unitData.depositAmount = parseFloat(unitForm.depositAmount);
        unitData.pricePerNight = null;
        unitData.checkoutTime = null;
      }

      // Add BNB-specific fields
      if (isBNB) {
        unitData.pricePerNight = parseFloat(unitForm.pricePerNight);
        unitData.checkoutTime = unitForm.checkoutTime;
        unitData.monthlyRent = null;
        unitData.depositAmount = null;
      }

      // Create unit - use currentPropertyId to ensure correct property ID
      console.log('Creating unit with propertyId:', currentPropertyId);
      console.log('Unit data:', unitData);
      const response = await createUnit(currentPropertyId, unitData);
      console.log('Create unit response:', response);

      if (response.status === 'success' && response.data) {
        if (currentPropertyId) {
          if (isBNB) setPropertyScope(currentPropertyId, 'bnb');
          else if (isRental) setPropertyScope(currentPropertyId, 'landlord');
        }
        // Use the created unit from the response and add it to existing units
        const createdUnit = normalizeUnitFromApi(response.data);
        setProperty((prevProperty) => {
          if (!prevProperty) return prevProperty;
          const existingUnits = prevProperty.units || [];
          // Check if unit already exists (avoid duplicates)
          const unitExists = existingUnits.some(u => u.id === createdUnit.id);
          if (unitExists) {
            return prevProperty;
          }
          return {
            ...prevProperty,
            units: [...existingUnits, createdUnit]
          };
        });

        // Reset form and close modal
        setUnitForm({
          unitNumber: '',
          name: '',
          description: '',
          unitTypeDefinitionId: '',
          unitCategoryDefinitionId: '',
          unitSubCategoryId: '',
          bedrooms: 0,
          bathrooms: 0,
          sizeSqm: '',
          amenities: [],
          photoFiles: [],
          photoPreviews: [],
          monthlyRent: '',
          depositAmount: '',
          rentDueDay: '',
          pricePerNight: '',
          checkoutTime: '',
          isPublic: true
        });
        setShowAddUnit(false);
      } else {
        throw new Error(response.message || 'Failed to create unit');
      }
    } catch (error) {
      console.error('Error creating unit:', error);
      setUnitErrorMessage(error.message || 'Failed to create unit. Please try again.');
    } finally {
      setSubmittingUnit(false);
    }
  };

  const handleBulkUnitSubmit = async (e) => {
    e.preventDefault();

    // Ensure we're using the correct propertyId - use property.id as source of truth
    const currentPropertyId = property?.id || propertyId;
    if (!currentPropertyId) {
      setBulkUnitErrorMessage('Property ID is missing. Please refresh the page.');
      return;
    }

    // Log for debugging
    console.log('handleBulkUnitSubmit - propertyId prop:', propertyId);
    console.log('handleBulkUnitSubmit - property.id:', property?.id);
    console.log('handleBulkUnitSubmit - using propertyId:', currentPropertyId);

    setSubmittingBulkUnits(true);
    setBulkUnitErrorMessage('');

    try {
      // Validate required template fields
      if (!bulkUnitForm.name) {
        throw new Error('Unit name template is required');
      }

      if (!bulkUnitForm.unitTypeDefinitionId || !bulkUnitForm.unitCategoryDefinitionId || !bulkUnitForm.unitSubCategoryId) {
        throw new Error('Unit type, category, and subcategory are required');
      }

      // Validate photos (2-6 required)
      if (bulkUnitForm.photoFiles.length < 2) {
        throw new Error('At least 2 photos are required (maximum 6)');
      }

      if (bulkUnitForm.photoFiles.length > 6) {
        throw new Error('Maximum 6 photos allowed');
      }

      // Generate unit numbers
      let unitNumbers = [];
      if (!bulkUnitForm.unitNumbersInput || !bulkUnitForm.unitNumbersInput.trim()) {
        throw new Error('Please enter unit numbers (comma-separated)');
      }
      unitNumbers = bulkUnitForm.unitNumbersInput
        .split(',')
        .map(num => num.trim())
        .filter(Boolean);

      if (unitNumbers.length === 0 || unitNumbers.length > 100) {
        throw new Error('Please provide 1-100 unit numbers');
      }

      // De-duplicate while preserving order
      unitNumbers = Array.from(new Set(unitNumbers));

      // Validate based on subcategory
      const selectedSubcategory = unitSubcategories.find(sub => sub.id === bulkUnitForm.unitSubCategoryId);
      const isRental = selectedSubcategory?.name?.toLowerCase() === 'rental';
      const isBNB = selectedSubcategory?.name?.toLowerCase() === 'bnb' || selectedSubcategory?.name?.toLowerCase() === 'airbnb';

      if (isRental) {
        if (!bulkUnitForm.monthlyRent || parseFloat(bulkUnitForm.monthlyRent) <= 0) {
          throw new Error('Monthly rent is required for rental units');
        }
        if (!bulkUnitForm.depositAmount || parseFloat(bulkUnitForm.depositAmount) <= 0) {
          throw new Error('Deposit amount is required for rental units');
        }
      }

      if (isBNB) {
        if (!bulkUnitForm.pricePerNight || parseFloat(bulkUnitForm.pricePerNight) <= 0) {
          throw new Error('Price per night is required for BNB units');
        }
        if (!bulkUnitForm.checkoutTime) {
          throw new Error('Checkout time is required for BNB units (e.g., 11:00)');
        }
      }

      // Upload photos first
      const photoUrls = [];
      for (let i = 0; i < bulkUnitForm.photoFiles.length; i++) {
        const file = bulkUnitForm.photoFiles[i];
        const uploadResponse = await uploadUnitImage(file);
        if (uploadResponse.status === 'success' && uploadResponse.data?.url) {
          photoUrls.push(uploadResponse.data.url);
        } else {
          throw new Error('Failed to upload one or more photos');
        }
      }

      // Prepare template
      const template = {
        name: bulkUnitForm.name,
        description: bulkUnitForm.description || '',
        unitTypeDefinitionId: bulkUnitForm.unitTypeDefinitionId,
        unitCategoryDefinitionId: bulkUnitForm.unitCategoryDefinitionId,
        unitSubCategoryId: bulkUnitForm.unitSubCategoryId,
        bedrooms: parseInt(bulkUnitForm.bedrooms) || 0,
        bathrooms: parseInt(bulkUnitForm.bathrooms) || 0,
        sizeSqm: parseFloat(bulkUnitForm.sizeSqm) || 0,
        amenities: bulkUnitForm.amenities,
        photos: photoUrls,
        isPublic: !!bulkUnitForm.isPublic
      };

      const rentDueDay = bulkUnitForm.rentDueDay === '' ? null : parseInt(bulkUnitForm.rentDueDay);
      if (Number.isFinite(rentDueDay) && rentDueDay !== null) {
        if (rentDueDay < 1 || rentDueDay > 28) {
          throw new Error('Rent due day must be between 1 and 28');
        }
        template.rentDueDay = rentDueDay;
      }

      // Add rental-specific fields
      if (isRental) {
        template.monthlyRent = parseFloat(bulkUnitForm.monthlyRent);
        template.depositAmount = parseFloat(bulkUnitForm.depositAmount);
        template.pricePerNight = null;
        template.checkoutTime = null;
      }

      // Add BNB-specific fields
      if (isBNB) {
        template.pricePerNight = parseFloat(bulkUnitForm.pricePerNight);
        template.checkoutTime = bulkUnitForm.checkoutTime;
        template.monthlyRent = null;
        template.depositAmount = null;
      }

      // Create bulk units - use currentPropertyId to ensure correct property ID
      console.log('Creating bulk units with propertyId:', currentPropertyId);
      console.log('Bulk data:', { template, unitNumbers });
      const response = await createBulkUnits(currentPropertyId, {
        template: template,
        unitNumbers: unitNumbers
      });
      console.log('Create bulk units response:', response);

      if (response.status === 'success' && response.data) {
        if (currentPropertyId) {
          if (isBNB) setPropertyScope(currentPropertyId, 'bnb');
          else if (isRental) setPropertyScope(currentPropertyId, 'landlord');
        }
        // Use the created units from the response and add them to existing units
        const createdUnits = Array.isArray(response.data) ? response.data : [];
        console.log('Created units:', createdUnits.length);
        setProperty((prevProperty) => {
          if (!prevProperty) return prevProperty;
          const existingUnits = prevProperty.units || [];
          // Merge new units with existing ones, avoiding duplicates
          const existingUnitIds = new Set(existingUnits.map(u => u.id));
          const newUnits = createdUnits
            .filter(u => !existingUnitIds.has(u.id))
            .map(normalizeUnitFromApi);
          return {
            ...prevProperty,
            units: [...existingUnits, ...newUnits]
          };
        });

        // Reset form and close modal
        setBulkUnitForm({
          name: '',
          description: '',
          unitTypeDefinitionId: '',
          unitCategoryDefinitionId: '',
          unitSubCategoryId: '',
          bedrooms: 0,
          bathrooms: 0,
          sizeSqm: '',
          amenities: [],
          photoFiles: [],
          photoPreviews: [],
          monthlyRent: '',
          depositAmount: '',
          rentDueDay: '',
          pricePerNight: '',
          checkoutTime: '',
          isPublic: true,
          unitNumbersInput: ''
        });
        setShowBulkAddUnit(false);
      } else {
        throw new Error(response.message || 'Failed to create bulk units');
      }
    } catch (error) {
      console.error('Error creating bulk units:', error);
      setBulkUnitErrorMessage(error.message || 'Failed to create bulk units. Please try again.');
    } finally {
      setSubmittingBulkUnits(false);
    }
  };

  const handleLinkTenant = async (e, unitId) => {
    e.preventDefault();
    setLinkTenantError('');

    const userId = String(tenantForm.tenantId || '').trim();
    const tenantName = String(tenantForm.tenantName || '').trim();
    if (!userId || !tenantName) {
      setLinkTenantError('Tenant name and tenant ID are required.');
      return;
    }

    const currentPropertyId = property?.id || propertyId;
    if (!currentPropertyId) {
      setLinkTenantError('Property ID is missing. Please refresh the page.');
      return;
    }

    setLinkingTenant(true);
    try {
      await assignTenantToUnit(currentPropertyId, unitId, { userId, agreementAccepted: true });
      try {
        await updateUnit(currentPropertyId, unitId, { isPublic: false });
      } catch (listingErr) {
        console.error('Could not set unit listing to private:', listingErr);
      }

      const unitsArrayRaw = await fetchUnitsForProperty(currentPropertyId);
      const unitsArray = await hydrateUnitsWithActiveTenancy(unitsArrayRaw);

      setProperty((prev) => {
        if (!prev) return prev;
        if (unitsArray.length > 0) {
          return { ...prev, units: unitsArray };
        }
        const updatedUnits = (prev.units || []).map((unit) =>
          unit.id === unitId
            ? {
              ...unit,
              tenantId: userId,
              tenantName,
              status: 'occupied',
              isPublic: false,
              listingType: 'private',
              tenantLinkedDate: new Date().toISOString(),
              moveInDate: new Date().toISOString().split('T')[0],
            }
            : unit
        );
        return { ...prev, units: updatedUnits };
      });

      setShowLinkTenant(null);
      setTenantForm({ tenantId: '', tenantName: '' });
    } catch (error) {
      console.error('Error linking tenant:', error);
      setLinkTenantError(error?.message || 'Failed to link tenant. Please try again.');
    } finally {
      setLinkingTenant(false);
    }
  };

  const handleUpdateUnit = async (e) => {
    e.preventDefault();
    if (!showUpdateUnit) return;

    // Ensure we're using the correct propertyId - use property.id as source of truth
    const currentPropertyId = property?.id || propertyId;
    if (!currentPropertyId) {
      setUpdateUnitErrorMessage('Property ID is missing. Please refresh the page.');
      return;
    }

    // Log for debugging
    console.log('handleUpdateUnit - propertyId prop:', propertyId);
    console.log('handleUpdateUnit - property.id:', property?.id);
    console.log('handleUpdateUnit - using propertyId:', currentPropertyId);
    console.log('handleUpdateUnit - unitId:', showUpdateUnit);

    setUpdatingUnit(true);
    setUpdateUnitErrorMessage('');

    try {
      // Validate photos (2-6 required if photos are being updated)
      const totalPhotos = updateUnitForm.existingPhotos.length + updateUnitForm.photoFiles.length;
      if (totalPhotos > 0) {
        if (totalPhotos < 2) {
          throw new Error('At least 2 photos are required (maximum 6)');
        }
        if (totalPhotos > 6) {
          throw new Error('Maximum 6 photos allowed');
        }
      }

      // Upload new photos if any
      const newPhotoUrls = [];
      for (let i = 0; i < updateUnitForm.photoFiles.length; i++) {
        const file = updateUnitForm.photoFiles[i];
        const uploadResponse = await uploadUnitImage(file);
        if (uploadResponse.status === 'success' && uploadResponse.data?.url) {
          newPhotoUrls.push(uploadResponse.data.url);
        } else {
          throw new Error('Failed to upload one or more photos');
        }
      }

      // Combine existing and new photos
      const allPhotoUrls = [...updateUnitForm.existingPhotos, ...newPhotoUrls];

      // Prepare unit update data - only include fields that are in the API
      const unitData = {};

      // Add fields only if they have values (all optional)
      if (updateUnitForm.unitNumber) {
        unitData.unitNumber = updateUnitForm.unitNumber;
      }
      if (updateUnitForm.name) {
        unitData.name = updateUnitForm.name;
      }
      if (updateUnitForm.description) {
        unitData.description = updateUnitForm.description;
      }
      if (allPhotoUrls.length > 0) {
        unitData.photos = allPhotoUrls;
      }

      // Add type/category/subcategory if selected
      if (updateUnitForm.unitTypeDefinitionId) {
        unitData.unitTypeDefinitionId = updateUnitForm.unitTypeDefinitionId;
      }
      if (updateUnitForm.unitCategoryDefinitionId) {
        unitData.unitCategoryDefinitionId = updateUnitForm.unitCategoryDefinitionId;
      }
      if (updateUnitForm.unitSubCategoryId) {
        unitData.unitSubCategoryId = updateUnitForm.unitSubCategoryId;
      }

      // Optional fields
      const rentDueDay = updateUnitForm.rentDueDay === '' ? null : parseInt(updateUnitForm.rentDueDay);
      if (Number.isFinite(rentDueDay) && rentDueDay !== null) {
        if (rentDueDay < 1 || rentDueDay > 28) {
          throw new Error('Rent due day must be between 1 and 28');
        }
        unitData.rentDueDay = rentDueDay;
      }
      if (updateUnitForm.isPublic !== undefined) {
        unitData.isPublic = !!updateUnitForm.isPublic;
      }

      // Determine unit type from existing unit data
      const isRental = selectedUnitForUpdate?.monthlyRent !== null && selectedUnitForUpdate?.monthlyRent !== undefined;
      const isBNB = selectedUnitForUpdate?.pricePerNight !== null && selectedUnitForUpdate?.pricePerNight !== undefined;

      // Add rental/BNB fields based on unit type
      if (isRental) {
        // For rental units, include monthlyRent and depositAmount if provided
        if (updateUnitForm.monthlyRent) {
          unitData.monthlyRent = parseFloat(updateUnitForm.monthlyRent);
        }
        if (updateUnitForm.depositAmount) {
          unitData.depositAmount = parseFloat(updateUnitForm.depositAmount);
        }
        // Always set BNB fields to null for rental units
        unitData.pricePerNight = null;
        unitData.checkoutTime = null;
      } else if (isBNB) {
        // For BNB units, include pricePerNight and checkoutTime if provided
        if (updateUnitForm.pricePerNight) {
          unitData.pricePerNight = parseFloat(updateUnitForm.pricePerNight);
        }
        if (updateUnitForm.checkoutTime) {
          unitData.checkoutTime = updateUnitForm.checkoutTime;
        }
        // Always set rental fields to null for BNB units
        unitData.monthlyRent = null;
        unitData.depositAmount = null;
      } else {
        // If type is unclear, include fields if provided (shouldn't happen, but handle gracefully)
        if (updateUnitForm.monthlyRent) {
          unitData.monthlyRent = parseFloat(updateUnitForm.monthlyRent);
        }
        if (updateUnitForm.depositAmount) {
          unitData.depositAmount = parseFloat(updateUnitForm.depositAmount);
        }
        if (updateUnitForm.pricePerNight) {
          unitData.pricePerNight = parseFloat(updateUnitForm.pricePerNight);
        }
        if (updateUnitForm.checkoutTime) {
          unitData.checkoutTime = updateUnitForm.checkoutTime;
        }
      }

      // Add isAvailable field
      unitData.isAvailable = updateUnitForm.isAvailable !== undefined ? updateUnitForm.isAvailable : true;

      // Update unit - use currentPropertyId to ensure correct property ID
      console.log('Updating unit with propertyId:', currentPropertyId);
      console.log('Unit data:', unitData);
      const response = await updateUnit(currentPropertyId, showUpdateUnit, unitData);

      if (response.status === 'success') {
        if (currentPropertyId) {
          if (isBNB) setPropertyScope(currentPropertyId, 'bnb');
          else if (isRental) setPropertyScope(currentPropertyId, 'landlord');
        }
        // Refresh property data to get updated units list
        const refreshResponse = await getPropertyById(currentPropertyId, true, {
          dashboardScope: String(propertyScope).toLowerCase() === 'bnb' ? 'BNB' : 'RENTAL',
        });
        if (refreshResponse.status === 'success' && refreshResponse.data) {
          const propertyData = refreshResponse.data;

          // Fetch units separately
          const units = await fetchUnitsForProperty(currentPropertyId);
          propertyData.units = await hydrateUnitsWithActiveTenancy(units);
          propertyData.unitCount = propertyData.units.length;
          setProperty(propertyData);
        }

        // Reset form and close modal
        setUpdateUnitForm({
          unitNumber: '',
          name: '',
          description: '',
          unitTypeDefinitionId: '',
          unitCategoryDefinitionId: '',
          unitSubCategoryId: '',
          photoFiles: [],
          photoPreviews: [],
          existingPhotos: [],
          monthlyRent: '',
          depositAmount: '',
          rentDueDay: '',
          pricePerNight: '',
          checkoutTime: '',
          isAvailable: true,
          isPublic: true
        });
        setSelectedUnitForUpdate(null);
        setShowUpdateUnit(null);
      } else {
        throw new Error(response.message || 'Failed to update unit');
      }
    } catch (error) {
      console.error('Error updating unit:', error);
      setUpdateUnitErrorMessage(error.message || 'Failed to update unit. Please try again.');
    } finally {
      setUpdatingUnit(false);
    }
  };

  const handleBulkEdit = (e) => {
    e.preventDefault();
    // TODO: Implement API call to bulk update units when endpoint is available

    const unitsToEdit = bulkEditForm.applyToAll
      ? (property.units || [])
      : (property.units || []).filter((u) => selectedUnitIds.includes(u.id));

    if (unitsToEdit.length === 0) {
      alert('Please select at least one unit to edit');
      return;
    }

    const updatedUnits = (property.units || []).map((unit) => {
      const shouldEdit = unitsToEdit.some((u) => u.id === unit.id);
      if (!shouldEdit) return unit;

      const updatedUnit = { ...unit };

      // Update unit type if provided
      if (bulkEditForm.unitType) {
        updatedUnit.unitType = bulkEditForm.unitType;
      }

      // Update unit number if prefix/suffix provided
      if (bulkEditForm.unitNumberPrefix || bulkEditForm.unitNumberSuffix) {
        const currentNumber = unit.unitNumber || '';
        let newNumber = currentNumber;

        if (bulkEditForm.unitNumberPrefix) {
          const numericPart = currentNumber.replace(/^[A-Za-z]*/, '');
          newNumber = `${bulkEditForm.unitNumberPrefix}${numericPart}`;
        }

        if (bulkEditForm.unitNumberSuffix) {
          newNumber = `${newNumber}${bulkEditForm.unitNumberSuffix}`;
        }

        updatedUnit.unitNumber = newNumber;
      }

      // Update rent if provided
      if (bulkEditForm.rent && bulkEditForm.rent.trim() !== '') {
        updatedUnit.rent = parseFloat(bulkEditForm.rent);
      }

      return updatedUnit;
    });

    setProperty({
      ...property,
      units: updatedUnits
    });
    setShowBulkEdit(false);
    setSelectedUnitIds([]);
    setBulkEditForm({
      unitType: '',
      unitNumberPrefix: '',
      unitNumberSuffix: '',
      rent: '',
      applyToAll: false
    });
    alert(`Successfully updated ${unitsToEdit.length} unit(s)!`);
  };

  const toggleListingType = (unitId) => {
    // Backed by API now via unit.isPublic
    const currentPropertyId = property?.id || propertyId;
    if (!currentPropertyId) return;

    const unit = (property.units || []).find(u => u.id === unitId);
    if (!unit) return;

    if (unitHasAssignedTenant(normalizeUnitFromApi(unit))) {
      return;
    }

    const currentIsPublic = unit.isPublic !== undefined ? !!unit.isPublic : unit.listingType === 'public';
    const nextIsPublic = !currentIsPublic;

    // Optimistic UI update
    setProperty((prev) => {
      if (!prev) return prev;
      const updatedUnits = (prev.units || []).map(u => (u.id === unitId ? { ...u, isPublic: nextIsPublic } : u));
      return { ...prev, units: updatedUnits };
    });

    // Persist
    updateUnit(currentPropertyId, unitId, { isPublic: nextIsPublic }).catch((error) => {
      console.error('Error updating unit listing:', error);
      // Rollback on failure
      setProperty((prev) => {
        if (!prev) return prev;
        const updatedUnits = (prev.units || []).map(u => (u.id === unitId ? { ...u, isPublic: currentIsPublic } : u));
        return { ...prev, units: updatedUnits };
      });
      setUpdateUnitErrorMessage(error?.message || 'Failed to update unit listing. Please try again.');
    });
  };

  const getTotalDamagesForUnit = (unit) => {
    if (!unit.damages || unit.damages.length === 0) return 0;
    return unit.damages.reduce((sum, damage) => sum + (damage.amount || 0), 0);
  };

  const handleDeleteUnit = (unitId, e) => {
    e?.stopPropagation?.();
    setDeleteUnitError('');
    setUnitToDelete(unitId);
  };

  const handleConfirmDeleteUnit = async () => {
    if (!unitToDelete || deletingUnit) return;

    const currentPropertyId = property?.id || propertyId;
    if (!currentPropertyId) return;

    const deletedUnitId = unitToDelete;
    setDeletingUnit(true);
    setDeleteUnitError('');
    try {
      try {
        await updateUnit(currentPropertyId, deletedUnitId, {
          isPublic: false,
          isAvailable: false,
        });
      } catch (_) {
        // Best-effort unpublish before delete so public/tenant listings update immediately
      }
      try {
        await deleteUnit(currentPropertyId, deletedUnitId);
      } catch (error) {
        const isNotFound =
          error?.status === 404 ||
          /not found/i.test(String(error?.message || ''));
        if (!isNotFound) {
          throw error;
        }
      }

      await refreshPropertyUnits();
      setSelectedUnitIds((prev) => prev.filter((id) => !unitMatchesId(id, deletedUnitId)));
      setUnitToDelete(null);
    } catch (error) {
      setDeleteUnitError(error.message || 'Failed to delete unit. Please try again.');
    } finally {
      setDeletingUnit(false);
    }
  };

  const handleCancelDeleteUnit = () => {
    if (deletingUnit) return;
    setDeleteUnitError('');
    setUnitToDelete(null);
  };

  const propertyUnits = filterUnitsByListingScope(
    (property?.units || []).map(normalizeUnitFromApi),
    String(propertyScope).toLowerCase() === 'bnb' ? 'BNB' : 'RENTAL'
  );
  const isBnbScope = String(propertyScope).toLowerCase() === 'bnb';
  const activeUnitFilterOptions = isBnbScope
    ? UNIT_FILTER_OPTIONS.filter((option) => !['paid', 'unpaid'].includes(option.value))
    : UNIT_FILTER_OPTIONS;

  const {
    totalUnits: headerTotalUnits,
    occupiedCount,
    vacantCount,
    damagedCount,
  } = computePropertyUnitStats(
    { ...property, units: propertyUnits },
    { propertyScope, bnbOccupiedUnitIds: bnbOccupiedUnitIds }
  );

  const rentPaymentCounts = extractRentPaymentCounts(rentStatusData);
  const unitPaymentStatusLookup = buildUnitPaymentStatusLookup(rentStatusData);

  const handleOpenUpdateUnitForm = (unit) => {
    setShowUpdateUnit(unit.id);
    const selectedUnit = propertyUnits.find((u) => u.id === unit.id);
    if (!selectedUnit) return;

    setSelectedUnitForUpdate(selectedUnit);
    setUpdateUnitForm({
      unitNumber: selectedUnit.unitNumber || '',
      name: selectedUnit.name || '',
      description: selectedUnit.description || '',
      unitTypeDefinitionId: selectedUnit.unitTypeDefinitionId || selectedUnit.unitTypeId || '',
      unitCategoryDefinitionId: selectedUnit.unitCategoryDefinitionId || selectedUnit.unitCategoryId || '',
      unitSubCategoryId: selectedUnit.unitSubCategoryId || selectedUnit.unitSubcategoryId || '',
      photoFiles: [],
      photoPreviews: [],
      existingPhotos: selectedUnit.photos || [],
      monthlyRent: selectedUnit.monthlyRent || '',
      depositAmount: selectedUnit.depositAmount || '',
      rentDueDay: selectedUnit.rentDueDay !== undefined && selectedUnit.rentDueDay !== null
        ? selectedUnit.rentDueDay.toString()
        : '',
      pricePerNight: selectedUnit.pricePerNight || '',
      checkoutTime: selectedUnit.checkoutTime || '',
      isAvailable: selectedUnit.isAvailable !== undefined ? selectedUnit.isAvailable : true,
      isPublic: selectedUnit.isPublic !== undefined ? !!selectedUnit.isPublic : true,
    });

    const loadUnitPhotos = async () => {
      const newAuthenticatedPhotos = {};
      const unitPhotos = selectedUnit.photos || [];

      for (let photoIndex = 0; photoIndex < unitPhotos.length; photoIndex++) {
        const photoUrl = unitPhotos[photoIndex];
        const photoKey = `photo_${photoIndex}`;

        if (photoUrl && (photoUrl.startsWith('blob:') || photoUrl.startsWith('data:'))) {
          newAuthenticatedPhotos[photoKey] = photoUrl;
          continue;
        }

        if (!photoUrl || !photoUrl.startsWith('http')) continue;

        try {
          const cachedImage = getImageFromCache(photoUrl);
          if (cachedImage) {
            newAuthenticatedPhotos[photoKey] = cachedImage;
          } else {
            const blobUrl = await fetchAuthenticatedImage(photoUrl);
            if (blobUrl) {
              newAuthenticatedPhotos[photoKey] = blobUrl;
              await storeImageInCache(photoUrl, blobUrl);
            }
          }
        } catch (error) {
          console.error(`Error loading unit photo ${photoIndex}:`, error);
        }
      }

      setAuthenticatedUnitPhotos(newAuthenticatedPhotos);
    };

    loadUnitPhotos();
  };

  const rentPeriodNow = new Date();
  const rentPeriodMonth = rentStatusData?.filterMonth ?? rentPeriodNow.getMonth() + 1;
  const rentPeriodYear = rentStatusData?.filterYear ?? rentPeriodNow.getFullYear();
  const rentPeriodLabel = new Date(rentPeriodYear, rentPeriodMonth - 1).toLocaleString('default', {
    month: 'short',
    year: 'numeric',
  });

  // Helper: check if a unit is occupied (BNB uses booking dates, rentals use tenant)
  const isUnitOccupied = (unit) => {
    if (isBnbScope) {
      const unitId = getUnitCanonicalId(unit);
      return (unitId && bnbOccupiedUnitIds.has(unitId)) || Boolean(String(unit?.tenantName || '').trim() || String(unit?.tenantId || '').trim());
    }
    return Boolean(unit.tenantName || unit.tenantId);
  };
  // Use monthlyRent from API response, fallback to rent for backward compatibility
  const totalRent = propertyUnits.reduce((sum, unit) => sum + (unit.monthlyRent || unit.rent || 0), 0);

  const filteredUnits = propertyUnits.filter((unit) => {
    // Apply unit filter
    if (unitFilter !== 'all') {
      const isPublic = unitListingIsPublic(unit);
      if (unitFilter === 'private' && isPublic) return false;
      if (unitFilter === 'public' && !isPublic) return false;
      // Vacant: no tenant assigned (and not BNB-booked)
      if (unitFilter === 'vacant' && isUnitOccupied(unit)) return false;
      // Occupied: has tenant assigned (or BNB-booked today)
      if (unitFilter === 'occupied' && !isUnitOccupied(unit)) return false;
      if (unitFilter === 'damaged' && unit.status !== 'damaged') return false;
      if (unitFilter === 'paid' || unitFilter === 'unpaid') {
        const paymentStatus = getUnitPaymentStatus(unit, unitPaymentStatusLookup);
        if (unitFilter === 'paid' && paymentStatus !== 'PAID') return false;
        if (unitFilter === 'unpaid' && paymentStatus !== 'UNPAID') return false;
      }
    }

    // Apply search query filter
    if (unitSearchQuery.trim()) {
      const query = unitSearchQuery.toLowerCase().trim();
      const unitNumberMatch = unit.unitNumber?.toLowerCase().includes(query);
      const unitNameMatch = unit.name?.toLowerCase().includes(query);
      const tenantNameMatch = unit.tenantName?.toLowerCase().includes(query);
      const tenantIdMatch = String(unit.tenantId || '').toLowerCase().includes(query);

      if (!unitNumberMatch && !unitNameMatch && !tenantNameMatch && !tenantIdMatch) {
        return false;
      }
    }

    return true;
  });

  const allFilteredUnitIds = filteredUnits.map((u) => getUnitCanonicalId(u)).filter(Boolean);
  const allFilteredUnitsSelected =
    allFilteredUnitIds.length > 0 &&
    allFilteredUnitIds.every((id) => selectedUnitIds.some((selectedId) => unitMatchesId(selectedId, id)));

  const handleSelectAllUnits = () => {
    if (allFilteredUnitsSelected) {
      setSelectedUnitIds([]);
    } else {
      setSelectedUnitIds(allFilteredUnitIds);
    }
  };

  const addressTrimmed = property.address?.trim();
  const cityTrimmed = property.location?.city?.trim();
  const showLocationLine = !!(addressTrimmed || cityTrimmed);
  const locationPrimary = addressTrimmed || cityTrimmed || '';
  const showCityBadge =
    !!cityTrimmed &&
    (!addressTrimmed || addressTrimmed.toLowerCase() !== cityTrimmed.toLowerCase());

  return (
    <>
    {!editModalOnly && (
    <div className="property-details-container">
      {errorMessage && (
        <div className="error-message" style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#fee',
          color: '#c33',
          borderRadius: '4px'
        }}>
          {errorMessage}
        </div>
      )}

      {/* Header */}
      <div className="property-details-header-compact">
        <div className="property-details-header-main">
          {onBack && (
            <button className="btn-back-to-properties" onClick={onBack} type="button">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              <span>Back to Properties</span>
            </button>
          )}
          <h1 className="page-title">{property.name}</h1>
          <div className="property-rating-detail">
            <div className="property-stars">
              {[1, 2, 3, 4, 5].map((s) => (
                <FaStar key={s} className={`tiny-star ${s <= (property.avgRating || 0) ? 'active' : 'empty'}`} />
              ))}
            </div>
            {property.ratingCount > 0 && (
              <span className="property-rating-count">
                {(property.avgRating || 0).toFixed(1)} ({property.ratingCount} reviews)
              </span>
            )}
            {property.ratingCount === 0 && (
              <span className="no-ratings-text">No reviews yet</span>
            )}
          </div>
          {showLocationLine && !showCityBadge && (
            <p className="property-address">{locationPrimary}</p>
          )}
          {property.assignedAgentName && (
            <p className="property-agent">
              Assigned Agent: {property.assignedAgentName}
            </p>
          )}
        </div>
      </div>

      <div className="property-overview-panel">
        <div className="property-overview-top">
          <div className="property-location-card">
            <div className="property-location-card__icon" aria-hidden>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <div className="property-location-card__content">
              <span className="property-location-card__city">
                {cityTrimmed || locationPrimary || property.name}
              </span>
              {showCityBadge && addressTrimmed && (
                <span className="property-location-card__address">{addressTrimmed}</span>
              )}
              <span className="property-location-card__income-label">Potential Monthly Income</span>
              <span className="property-location-card__income-value">
                KES {totalRent.toLocaleString()}
              </span>
            </div>
          </div>

          <div className={`property-dashboard-stats${rentStatusLoading ? ' property-dashboard-stats--loading' : ''}`}>
            <div className="property-dashboard-stat-card">
              <span className="property-dashboard-stat-label">Total Units</span>
              <span className="property-dashboard-stat-value">{headerTotalUnits}</span>
            </div>
            <div className="property-dashboard-stat-card success">
              <span className="property-dashboard-stat-label">Occupied</span>
              <span className="property-dashboard-stat-value">{occupiedCount}</span>
            </div>
            <div className="property-dashboard-stat-card warning">
              <span className="property-dashboard-stat-label">Vacant</span>
              <span className="property-dashboard-stat-value">{vacantCount}</span>
            </div>
            {!isBnbScope ? (
              <>
                <div className="property-dashboard-stat-card success">
                  <span className="property-dashboard-stat-label">Paid</span>
                  <span className="property-dashboard-stat-value">{rentPaymentCounts.paid}</span>
                </div>
                <div className="property-dashboard-stat-card danger">
                  <span className="property-dashboard-stat-label">Unpaid</span>
                  <span className="property-dashboard-stat-value">{rentPaymentCounts.unpaid}</span>
                </div>
                <div className="property-dashboard-stat-card info">
                  <span className="property-dashboard-stat-label">Partial</span>
                  <span className="property-dashboard-stat-value">{rentPaymentCounts.partial}</span>
                </div>
              </>
            ) : (
              damagedCount > 0 && (
                <div className="property-dashboard-stat-card danger">
                  <span className="property-dashboard-stat-label">Damaged</span>
                  <span className="property-dashboard-stat-value">{damagedCount}</span>
                </div>
              )
            )}
          </div>
        </div>

        {!isBnbScope && (
          <div className="property-overview-meta property-overview-meta--report-only">
            <div className="property-rent-report-row">
              <span className="property-rent-period">Rent {rentPeriodLabel}</span>
              <button
                type="button"
                className="property-rent-report-link"
                onClick={() => setShowRentReport(true)}
              >
                Full rent report
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Units Table */}
      <div className="units-section">
        {evictionActionError ? (
          <div className="error-message" style={{ marginBottom: '12px' }}>
            {evictionActionError}
          </div>
        ) : null}
        {evictionActionMessage ? (
          <div
            className="empty-state"
            style={{ marginBottom: '12px', backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}
          >
            {evictionActionMessage}
          </div>
        ) : null}
        <div className="units-section-top">
          <div className="units-section-title-row">
            <h4 className="units-title">Units</h4>
            <div className="units-section-title-actions">
              <label className="units-select-all">
                <input
                  type="checkbox"
                  checked={allFilteredUnitsSelected}
                  onChange={handleSelectAllUnits}
                />
                <span>Select all</span>
              </label>
              <span className="units-count">{headerTotalUnits} units</span>
            </div>
          </div>

          <div className="unit-filter-pills" role="tablist" aria-label="Filter units">
            {activeUnitFilterOptions.map((option) => {
              const isActive = unitFilter === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`unit-filter-pill${isActive ? ' active' : ''}`}
                  onClick={() => setUnitFilter(option.value)}
                >
                  {isActive && (
                    <svg className="unit-filter-pill-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                  {option.label}
                </button>
              );
            })}
          </div>

          <div className="unit-search-container unit-search-container--icon">
            <svg className="unit-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="unit-search-input"
              placeholder="Search units or tenants..."
              value={unitSearchQuery}
              onChange={(e) => setUnitSearchQuery(e.target.value)}
            />
          </div>

          <div className="units-action-row">
            <button
              type="button"
              className="btn-add-unit-primary"
              onClick={() => setShowAddUnit(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden>
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Unit
            </button>
            <button
              type="button"
              className="btn-bulk-add-secondary"
              onClick={() => setShowBulkAddUnit(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
              Bulk Add
            </button>
            {selectedUnitIds.length > 0 && (
              <button
                type="button"
                className="btn-bulk-edit-secondary"
                onClick={() => setShowBulkEdit(true)}
              >
                Bulk Edit ({selectedUnitIds.length})
              </button>
            )}
          </div>
        </div>

        {filteredUnits.length === 0 ? (
          <div className="units-empty-state">No units found</div>
        ) : (
          <div className="units-cards-list">
            {filteredUnits.map((unit) => {
              const unitRowId = getUnitCanonicalId(unit);
              const thumbnailUrl = getUnitThumbnailUrl(unit);
              const unitSubtitle = formatUnitSubtitle(unit);
              const occupancyStatus = isUnitOccupied(unit) ? 'occupied' : (unit.status || 'vacant');
              const paymentStatus = !isBnbScope
                ? getUnitPaymentStatus(unit, unitPaymentStatusLookup)
                : null;
              const damageTotal = getTotalDamagesForUnit(unit);
              const isPublic = unitListingIsPublic(unit);
              const hasTenant = unitHasAssignedTenant(unit);
              const rentAmount = unit.monthlyRent || unit.rent || unit.pricePerNight || 0;
              const rentLabel = isBnbScope && unit.pricePerNight ? '/night' : '/month';

              return (
                <article key={unitRowId} className="property-unit-card">
                  <div className="property-unit-card__select">
                    <input
                      type="checkbox"
                      checked={selectedUnitIds.some((id) => unitMatchesId(id, unitRowId))}
                      onChange={() => handleSelectUnit(unitRowId)}
                      aria-label={`Select unit ${unit.unitNumber || unitRowId}`}
                    />
                  </div>

                  <div className="property-unit-card__thumb">
                    {thumbnailUrl ? (
                      <img src={thumbnailUrl} alt="" loading="lazy" />
                    ) : (
                      <div className="property-unit-card__thumb-placeholder" aria-hidden>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M3 21h18M5 21V9l7-5 7 5v12M9 21v-6h6v6" />
                        </svg>
                      </div>
                    )}
                  </div>

                  <div className="property-unit-card__body">
                    <div className="property-unit-card__header">
                      <div className="property-unit-card__title-block">
                        <div className="property-unit-card__title-row">
                          <h5 className="property-unit-card__title">{formatUnitTitle(unit)}</h5>
                        </div>
                        {unitSubtitle && (
                          <p className="property-unit-card__subtitle">{unitSubtitle}</p>
                        )}
                      </div>
                      <span className={`status-badge property-unit-card__status ${occupancyStatus}`}>
                        {occupancyStatus}
                      </span>
                    </div>

                    <div className="property-unit-card__tenant-row">
                      {unit.tenantName || unit.tenantId ? (
                        <>
                          <svg className="property-unit-card__tenant-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                          <span className="tenant-name">{unit.tenantName || 'Tenant assigned'}</span>
                          {unit.tenantId && (
                            <span className="property-unit-card__tenant-id">({String(unit.tenantId).slice(0, 14)}{String(unit.tenantId).length > 14 ? '…' : ''})</span>
                          )}
                          {paymentStatus && (
                            <span className={`property-unit-card__payment-badge payment-${paymentStatus.toLowerCase()}`}>
                              {paymentStatus}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="no-tenant">No tenant assigned</span>
                      )}
                    </div>

                    <div className="property-unit-card__details-row">
                      {(unit.bedrooms > 0 || unit.bathrooms > 0) && (
                        <span className="property-unit-card__meta">
                          {unit.bedrooms > 0 && `${unit.bedrooms} bed`}
                          {unit.bedrooms > 0 && unit.bathrooms > 0 && ' · '}
                          {unit.bathrooms > 0 && `${unit.bathrooms} bath`}
                        </span>
                      )}
                      <span className="property-unit-card__meta">
                        Due: {unit.dueDate || 'N/A'}
                      </span>
                      <span className="property-unit-card__meta">
                        {damageTotal > 0 ? (
                          <span className="damage-amount">Damages: KES {damageTotal.toLocaleString()}</span>
                        ) : (
                          <span className="no-damage">No damages</span>
                        )}
                      </span>
                      <span className="property-unit-card__meta property-unit-card__listing">
                        {hasTenant ? (
                          <span className="listing-toggle private">🔒 Private</span>
                        ) : (
                          <button
                            type="button"
                            className={`listing-toggle ${isPublic ? 'public' : 'private'}`}
                            onClick={() => toggleListingType(unit.id)}
                          >
                            {isPublic ? '🌐 Public' : '🔒 Private'}
                          </button>
                        )}
                      </span>
                    </div>

                    <div className="property-unit-card__footer">
                      <span className="property-unit-card__rent">
                        KES {Number(rentAmount).toLocaleString()}
                        <span className="property-unit-card__rent-period">{rentLabel}</span>
                      </span>
                    </div>

                    <div className="property-unit-card__actions action-buttons">
                      {hasTenant && (
                        <>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm property-unit-card__action"
                            onClick={() => openReleaseModal(unit)}
                          >
                            Release
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm property-unit-card__action"
                            onClick={() => openEvictionModal(unit)}
                          >
                            Eviction
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm property-unit-card__action"
                        onClick={() => handleOpenUnitHistory(unit.id, unit.unitNumber || unit.name)}
                      >
                        History
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm property-unit-card__action"
                        onClick={() => handleOpenUpdateUnitForm(unit)}
                      >
                        Update Unit
                      </button>
                      {isUnitOccupied(unit) && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm property-unit-card__action property-unit-card__action--primary"
                          onClick={() => openRatingModal(unit)}
                        >
                          Rate Tenant
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm property-unit-card__action btn-delete-unit"
                        onClick={(e) => handleDeleteUnit(unitRowId, e)}
                        disabled={isUnitOccupied(unit)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
    )}

      {/* Edit Property Modal */}
      {showEditProperty && createPortal(
        <div className="modal-overlay" onClick={() => !updating && closeEditPropertyModal()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>Edit Property</h2>
              <button className="close-btn" onClick={closeEditPropertyModal} disabled={updating}>×</button>
            </div>
            {updateErrorMessage && (
              <div style={{ padding: '12px', margin: '16px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>
                {updateErrorMessage}
              </div>
            )}
            <form onSubmit={handleEditPropertySubmit} className="modal-form">
              <div className="form-group">
                <label>Property Name *</label>
                <input
                  type="text"
                  value={editPropertyForm.name}
                  onChange={(e) => setEditPropertyForm({ ...editPropertyForm, name: e.target.value })}
                  required
                  placeholder="Enter property name"
                  disabled={updating}
                />
              </div>

              <div className="form-group">
                <label>Address *</label>
                <input
                  type="text"
                  value={editPropertyForm.address}
                  onChange={(e) => setEditPropertyForm({ ...editPropertyForm, address: e.target.value })}
                  required
                  placeholder="Enter property address"
                  disabled={updating}
                />
              </div>

              <div className="form-group">
                <label>Location</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '8px' }}>
                  <div>
                    <input
                      type="number"
                      step="any"
                      value={editPropertyForm.location.lat}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        location: { ...editPropertyForm.location, lat: e.target.value }
                      })}
                      placeholder="Latitude"
                      disabled={updating}
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      step="any"
                      value={editPropertyForm.location.lng}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        location: { ...editPropertyForm.location, lng: e.target.value }
                      })}
                      placeholder="Longitude"
                      disabled={updating}
                    />
                  </div>
                </div>
                <input
                  type="text"
                  value={editPropertyForm.location.city}
                  onChange={(e) => setEditPropertyForm({
                    ...editPropertyForm,
                    location: { ...editPropertyForm.location, city: e.target.value }
                  })}
                  placeholder="City"
                  style={{ marginBottom: '8px' }}
                  disabled={updating}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    value={editPropertyForm.location.area}
                    onChange={(e) => setEditPropertyForm({
                      ...editPropertyForm,
                      location: { ...editPropertyForm.location, area: e.target.value }
                    })}
                    placeholder="Area (e.g. Westlands)"
                    disabled={updating}
                  />
                  <input
                    type="text"
                    value={editPropertyForm.location.suburb}
                    onChange={(e) => setEditPropertyForm({
                      ...editPropertyForm,
                      location: { ...editPropertyForm.location, suburb: e.target.value }
                    })}
                    placeholder="Suburb (optional)"
                    disabled={updating}
                  />
                </div>
                <input
                  type="text"
                  value={editPropertyForm.location.locality}
                  onChange={(e) => setEditPropertyForm({
                    ...editPropertyForm,
                    location: { ...editPropertyForm.location, locality: e.target.value }
                  })}
                  placeholder="Locality (optional)"
                  style={{ marginBottom: '8px' }}
                  disabled={updating}
                />
                <LocationPicker
                  location={editPropertyForm.location}
                  onLocationChange={(newLocation) => {
                    setEditPropertyForm((prev) => ({
                      ...prev,
                      location: { ...prev.location, ...newLocation }
                    }));
                  }}
                  height="350px"
                  disabled={updating}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editPropertyForm.description}
                  onChange={(e) => setEditPropertyForm({ ...editPropertyForm, description: e.target.value })}
                  rows="4"
                  placeholder="Enter property description"
                  disabled={updating}
                />
              </div>

              <div className="form-group">
                <label>Amenities</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {availableAmenities.map((amenity) => (
                    <label key={amenity} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={editPropertyForm.amenities.includes(amenity)}
                        onChange={() => toggleEditAmenity(amenity)}
                        disabled={updating}
                        style={{ marginRight: '6px' }}
                      />
                      <span style={{ textTransform: 'capitalize' }}>{amenity.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Property Images * (1-6 images)</label>
                {/* Show existing images */}
                {editPropertyForm.existingImages.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
                    {editPropertyForm.existingImages.map((imageUrl, index) => {
                      // Try to get authenticated image, fallback to URL
                      const imageKey = `img_${index}`;
                      const authenticatedUrl = authenticatedImages[imageKey] || imageUrl;
                      return (
                        <div key={index} style={{ position: 'relative' }}>
                          <img
                            src={authenticatedUrl}
                            alt={`Existing ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100px',
                              objectFit: 'cover',
                              borderRadius: 8,
                              border: '1px solid #e0e0e0'
                            }}
                            onError={(e) => {
                              const el = e.currentTarget;
                              el.onerror = null;
                              el.src = BROKEN_IMAGE_PLACEHOLDER_SRC;
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveEditImage(index, true)}
                            disabled={updating}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              background: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              cursor: 'pointer',
                              fontSize: '16px',
                              lineHeight: '1'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Show new image previews */}
                {editPropertyForm.imagePreviews.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
                    {editPropertyForm.imagePreviews.map((preview, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #e0e0e0'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveEditImage(index, false)}
                          disabled={updating}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            lineHeight: '1'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleEditPropertyImagesChange}
                  disabled={updating || (editPropertyForm.existingImages.length + editPropertyForm.imageFiles.length) >= 6}
                />
                <small className="form-hint">
                  {(editPropertyForm.existingImages.length + editPropertyForm.imageFiles.length)}/6 images selected
                </small>
              </div>

              <div className="form-group">
                <label>Unit Count *</label>
                <input
                  type="number"
                  min="1"
                  value={editPropertyForm.unitCount}
                  onChange={(e) => setEditPropertyForm({ ...editPropertyForm, unitCount: e.target.value })}
                  required
                  placeholder="Enter number of units"
                  disabled={updating}
                />
              </div>

              <div className="form-group">
                <label>Contacts</label>
                <input
                  type="tel"
                  value={editPropertyForm.contacts.phone}
                  onChange={(e) => setEditPropertyForm({
                    ...editPropertyForm,
                    contacts: { ...editPropertyForm.contacts, phone: e.target.value }
                  })}
                  placeholder="Phone number"
                  disabled={updating}
                />
                <input
                  type="email"
                  value={editPropertyForm.contacts.email}
                  onChange={(e) => setEditPropertyForm({
                    ...editPropertyForm,
                    contacts: { ...editPropertyForm.contacts, email: e.target.value }
                  })}
                  placeholder="Email"
                  style={{ marginTop: '8px' }}
                  disabled={updating}
                />
                <input
                  type="tel"
                  value={editPropertyForm.contacts.caretakerPhone}
                  onChange={(e) => setEditPropertyForm({
                    ...editPropertyForm,
                    contacts: { ...editPropertyForm.contacts, caretakerPhone: e.target.value }
                  })}
                  placeholder="Caretaker phone"
                  style={{ marginTop: '8px' }}
                  disabled={updating}
                />
              </div>

              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={editPropertyForm.paymentDetails.method}
                  onChange={(e) => setEditPropertyForm({
                    ...editPropertyForm,
                    paymentDetails: { ...editPropertyForm.paymentDetails, method: e.target.value }
                  })}
                  disabled={updating}
                >
                  {paymentMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>

                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '10px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!editPropertyForm.paymentDetails.acceptsPochi}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, acceptsPochi: e.target.checked }
                      })}
                      disabled={updating}
                      style={{ marginRight: '6px' }}
                    />
                    <span>Accepts Pochi</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!editPropertyForm.paymentDetails.acceptsSendMoney}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, acceptsSendMoney: e.target.checked }
                      })}
                      disabled={updating}
                      style={{ marginRight: '6px' }}
                    />
                    <span>Accepts Send Money</span>
                  </label>
                </div>

                {editPropertyForm.paymentDetails.method === 'PAYBILL' && (
                  <>
                    <input
                      type="text"
                      value={editPropertyForm.paymentDetails.paybillNumber}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, paybillNumber: e.target.value }
                      })}
                      placeholder="Paybill Number"
                      style={{ marginTop: '8px' }}
                      disabled={updating}
                    />
                    <input
                      type="text"
                      value={editPropertyForm.paymentDetails.paybillAccount}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, paybillAccount: e.target.value }
                      })}
                      placeholder="Paybill Account"
                      style={{ marginTop: '8px' }}
                      disabled={updating}
                    />
                    <input
                      type="text"
                      value={editPropertyForm.paymentDetails.accountName}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, accountName: e.target.value }
                      })}
                      placeholder="Account Name"
                      style={{ marginTop: '8px' }}
                      disabled={updating}
                    />
                  </>
                )}

                {editPropertyForm.paymentDetails.method === 'TILL' && (
                  <>
                    <input
                      type="text"
                      value={editPropertyForm.paymentDetails.tillNumber || ''}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, tillNumber: e.target.value }
                      })}
                      placeholder="Till Number"
                      style={{ marginTop: '8px' }}
                      disabled={updating}
                    />
                    <input
                      type="text"
                      value={editPropertyForm.paymentDetails.accountName}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, accountName: e.target.value }
                      })}
                      placeholder="Account Name"
                      style={{ marginTop: '8px' }}
                      disabled={updating}
                    />
                  </>
                )}

                {editPropertyForm.paymentDetails.method === 'POCHI' && (
                  <>
                    <input
                      type="tel"
                      value={editPropertyForm.paymentDetails.pochiNumber || ''}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, pochiNumber: e.target.value }
                      })}
                      placeholder="Pochi Number"
                      style={{ marginTop: '8px' }}
                      disabled={updating}
                    />
                    <input
                      type="text"
                      value={editPropertyForm.paymentDetails.accountName}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, accountName: e.target.value }
                      })}
                      placeholder="Account Name"
                      style={{ marginTop: '8px' }}
                      disabled={updating}
                    />
                  </>
                )}

                {editPropertyForm.paymentDetails.method === 'SEND_MONEY' && (
                  <>
                    <input
                      type="tel"
                      value={editPropertyForm.paymentDetails.phoneNumber || ''}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, phoneNumber: e.target.value }
                      })}
                      placeholder="Phone Number"
                      style={{ marginTop: '8px' }}
                      disabled={updating}
                    />
                    <input
                      type="text"
                      value={editPropertyForm.paymentDetails.accountName}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, accountName: e.target.value }
                      })}
                      placeholder="Account Name"
                      style={{ marginTop: '8px' }}
                      disabled={updating}
                    />
                  </>
                )}

                {editPropertyForm.paymentDetails.method === 'BANK' && (
                  <>
                    <input
                      type="text"
                      value={editPropertyForm.paymentDetails.bankName || ''}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, bankName: e.target.value }
                      })}
                      placeholder="Bank Name"
                      style={{ marginTop: '8px' }}
                      disabled={updating}
                    />
                    <input
                      type="text"
                      value={editPropertyForm.paymentDetails.accountName}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, accountName: e.target.value }
                      })}
                      placeholder="Account Name"
                      style={{ marginTop: '8px' }}
                      disabled={updating}
                    />
                    <input
                      type="text"
                      value={editPropertyForm.paymentDetails.accountNumber || ''}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, accountNumber: e.target.value }
                      })}
                      placeholder="Account Number"
                      style={{ marginTop: '8px' }}
                      disabled={updating}
                    />
                    <input
                      type="text"
                      value={editPropertyForm.paymentDetails.branch || ''}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, branch: e.target.value }
                      })}
                      placeholder="Branch"
                      style={{ marginTop: '8px' }}
                      disabled={updating}
                    />
                    <input
                      type="text"
                      value={editPropertyForm.paymentDetails.swiftCode || ''}
                      onChange={(e) => setEditPropertyForm({
                        ...editPropertyForm,
                        paymentDetails: { ...editPropertyForm.paymentDetails, swiftCode: e.target.value }
                      })}
                      placeholder="SWIFT Code"
                      style={{ marginTop: '8px' }}
                      disabled={updating}
                    />
                  </>
                )}

                {editPropertyForm.paymentDetails.method === 'CASH' && (
                  <textarea
                    value={editPropertyForm.paymentDetails.instructions || ''}
                    onChange={(e) => setEditPropertyForm({
                      ...editPropertyForm,
                      paymentDetails: { ...editPropertyForm.paymentDetails, instructions: e.target.value }
                    })}
                    placeholder="Payment instructions (e.g., Pay at caretaker office 8am–5pm)"
                    rows="3"
                    style={{ marginTop: '8px' }}
                    disabled={updating}
                  />
                )}
              </div>

              <div className="form-group">
                <label>Agreement Document (Optional)</label>
                {editPropertyForm.agreementUrl && !editPropertyForm.agreementFile && (
                  <div style={{ marginBottom: '8px' }}>
                    {authenticatedAgreementUrl ? (
                      <a
                        href={authenticatedAgreementUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#0066cc', textDecoration: 'underline' }}
                      >
                        View Current Agreement Document
                      </a>
                    ) : agreementFileMissing ? (
                      <p
                        style={{
                          margin: 0,
                          padding: '10px 12px',
                          background: '#fff8e6',
                          border: '1px solid #f0d78c',
                          borderRadius: 8,
                          color: '#6b5900',
                          fontSize: 13,
                          lineHeight: 1.45,
                        }}
                      >
                        The agreement file is not available from the server (for example 404 Not Found).
                        Your property still has a saved link, but the file is missing in storage or the path is wrong.
                        Upload a new agreement below, or ask your backend team to fix file storage for{' '}
                        <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{editPropertyForm.agreementUrl}</code>
                      </p>
                    ) : (
                      <span style={{ fontSize: 13, color: '#6b7280' }}>Loading agreement…</span>
                    )}
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleEditAgreementChange}
                  disabled={updating}
                />
                {editPropertyForm.agreementPreview && (
                  <div style={{ marginTop: '12px' }}>
                    <a
                      href={editPropertyForm.agreementPreview}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#0066cc', textDecoration: 'underline' }}
                    >
                      View Agreement Preview
                    </a>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Visibility</label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!editPropertyForm.published}
                    onChange={(e) => setEditPropertyForm({ ...editPropertyForm, published: e.target.checked })}
                    disabled={updating}
                    style={{ marginRight: '6px' }}
                  />
                  <span>Published (visible on public listing)</span>
                </label>
              </div>

              <div className="form-group">
                <label>Water billing</label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={!!editPropertyForm.waterIncludedInRent}
                    onChange={(e) =>
                      setEditPropertyForm({
                        ...editPropertyForm,
                        waterIncludedInRent: e.target.checked
                      })
                    }
                    disabled={updating}
                    style={{ marginRight: '6px' }}
                  />
                  <span>Water included in rent</span>
                </label>
                {!editPropertyForm.waterIncludedInRent && (
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={editPropertyForm.waterRatePerUnit}
                    onChange={(e) => setEditPropertyForm({ ...editPropertyForm, waterRatePerUnit: e.target.value })}
                    placeholder="Water rate per unit (e.g. 200)"
                    disabled={updating}
                  />
                )}
              </div>

              <div className="form-group">
                <label>Late Fee Settings (Optional)</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <input
                      type="number"
                      min="0"
                      value={editPropertyForm.rentGraceDays}
                      onChange={(e) => setEditPropertyForm({ ...editPropertyForm, rentGraceDays: e.target.value })}
                      placeholder="Rent grace days (e.g. 3)"
                      disabled={updating}
                    />
                  </div>
                  <div>
                    <select
                      value={editPropertyForm.lateFeeType}
                      onChange={(e) => setEditPropertyForm({ ...editPropertyForm, lateFeeType: e.target.value })}
                      disabled={updating}
                    >
                      <option value="FIXED_PER_DAY">Fixed per day</option>
                      <option value="PERCENT_OF_RENT_PER_DAY">Percent of rent per day</option>
                    </select>
                  </div>
                </div>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={editPropertyForm.lateFeeValue}
                  onChange={(e) => setEditPropertyForm({ ...editPropertyForm, lateFeeValue: e.target.value })}
                  placeholder="Late fee value (e.g. 200)"
                  style={{ marginTop: '8px' }}
                  disabled={updating}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeEditPropertyModal}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updating}
                >
                  {updating ? 'Updating...' : 'Update Property'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Modals - Same as ManageRentals */}
      {!editModalOnly && showAddUnit && createPortal(
        <div className="modal-overlay" onClick={() => !submittingUnit && setShowAddUnit(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>Add New Unit</h2>
              <button className="close-btn" onClick={() => !submittingUnit && setShowAddUnit(false)} disabled={submittingUnit}>×</button>
            </div>
            {unitErrorMessage && (
              <div style={{ padding: '12px', margin: '16px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>
                {unitErrorMessage}
              </div>
            )}
            <form onSubmit={handleUnitSubmit} className="modal-form">
              <div className="form-group">
                <label>Unit Number *</label>
                <input
                  type="text"
                  value={unitForm.unitNumber}
                  onChange={(e) => setUnitForm({ ...unitForm, unitNumber: e.target.value })}
                  required
                  placeholder="e.g., A1, 101"
                  disabled={submittingUnit}
                />
              </div>

              <div className="form-group">
                <label>Unit Name *</label>
                <input
                  type="text"
                  value={unitForm.name}
                  onChange={(e) => setUnitForm({ ...unitForm, name: e.target.value })}
                  required
                  placeholder="e.g., Unit A1 - Single Room"
                  disabled={submittingUnit}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={unitForm.description}
                  onChange={(e) => setUnitForm({ ...unitForm, description: e.target.value })}
                  rows="3"
                  placeholder="e.g., Spacious 1BR with balcony"
                  disabled={submittingUnit}
                />
              </div>

              <div className="form-group">
                <label>Unit Category *</label>
                <select
                  value={unitForm.unitCategoryDefinitionId}
                  onChange={(e) => setUnitForm({ ...unitForm, unitCategoryDefinitionId: e.target.value, unitSubCategoryId: '' })}
                  required
                  disabled={submittingUnit || loadingUnitCategories}
                >
                  <option value="">Select Category</option>
                  {unitCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {loadingUnitCategories && <small className="form-hint">Loading categories...</small>}
              </div>

              <div className="form-group">
                <label>Unit Subcategory *</label>
                <select
                  value={unitForm.unitSubCategoryId}
                  onChange={(e) => setUnitForm({ ...unitForm, unitSubCategoryId: e.target.value })}
                  required
                  disabled={submittingUnit || !unitForm.unitCategoryDefinitionId || loadingUnitSubcategories}
                >
                  <option value="">Select Subcategory</option>
                  {unitSubcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </option>
                  ))}
                </select>
                {loadingUnitSubcategories && <small className="form-hint">Loading subcategories...</small>}
                {!unitForm.unitCategoryDefinitionId && <small className="form-hint">Please select a category first</small>}
              </div>

              <div className="form-group">
                <label>Unit Type *</label>
                <select
                  value={unitForm.unitTypeDefinitionId}
                  onChange={(e) => setUnitForm({ ...unitForm, unitTypeDefinitionId: e.target.value })}
                  required
                  disabled={submittingUnit || loadingUnitTypes}
                >
                  <option value="">Select Unit Type</option>
                  {unitTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {loadingUnitTypes && <small className="form-hint">Loading unit types...</small>}
              </div>

              <div className="form-group">
                <label>Bedrooms</label>
                <input
                  type="number"
                  min="0"
                  value={unitForm.bedrooms}
                  onChange={(e) => setUnitForm({ ...unitForm, bedrooms: e.target.value })}
                  placeholder="0"
                  disabled={submittingUnit}
                />
              </div>

              <div className="form-group">
                <label>Bathrooms</label>
                <input
                  type="number"
                  min="0"
                  value={unitForm.bathrooms}
                  onChange={(e) => setUnitForm({ ...unitForm, bathrooms: e.target.value })}
                  placeholder="0"
                  disabled={submittingUnit}
                />
              </div>

              <div className="form-group">
                <label>Size (Square Meters)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={unitForm.sizeSqm}
                  onChange={(e) => setUnitForm({ ...unitForm, sizeSqm: e.target.value })}
                  placeholder="e.g., 45.5"
                  disabled={submittingUnit}
                />
              </div>

              <div className="form-group">
                <label>Rent Due Day (Optional)</label>
                <input
                  type="number"
                  min="1"
                  max="28"
                  value={unitForm.rentDueDay}
                  onChange={(e) => setUnitForm({ ...unitForm, rentDueDay: e.target.value })}
                  placeholder="1-28 (recommended)"
                  disabled={submittingUnit}
                />
                <small className="form-hint">Optional. Recommended range is 1-28.</small>
              </div>

              <div className="form-group">
                <label>Listing</label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!unitForm.isPublic}
                    onChange={(e) => setUnitForm({ ...unitForm, isPublic: e.target.checked })}
                    disabled={submittingUnit}
                    style={{ marginRight: '6px' }}
                  />
                  <span>Public (shown on public listing)</span>
                </label>
              </div>

              <div className="form-group">
                <label>Amenities</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {availableAmenities.map((amenity) => (
                    <label key={amenity} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={unitForm.amenities.includes(amenity)}
                        onChange={() => toggleUnitAmenity(amenity)}
                        disabled={submittingUnit}
                        style={{ marginRight: '6px' }}
                      />
                      <span style={{ textTransform: 'capitalize' }}>{amenity.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Unit Photos * (2-6 images)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleUnitPhotosChange}
                  disabled={submittingUnit || unitForm.photoFiles.length >= 6}
                />
                <small className="form-hint">
                  {unitForm.photoFiles.length}/6 photos selected (minimum 2 required)
                </small>
                {unitForm.photoPreviews.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '12px' }}>
                    {unitForm.photoPreviews.map((preview, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #e0e0e0'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveUnitPhoto(index)}
                          disabled={submittingUnit}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            lineHeight: '1'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Rental-specific fields */}
              {isRental && (
                <>
                  <div className="form-group">
                    <label>Monthly Rent (KES) *</label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={unitForm.monthlyRent}
                      onChange={(e) => setUnitForm({ ...unitForm, monthlyRent: e.target.value })}
                      required={isRental}
                      placeholder="e.g., 15000"
                      disabled={submittingUnit}
                    />
                  </div>

                  <div className="form-group">
                    <label>Deposit Amount (KES) *</label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={unitForm.depositAmount}
                      onChange={(e) => setUnitForm({ ...unitForm, depositAmount: e.target.value })}
                      required={isRental}
                      placeholder="e.g., 15000"
                      disabled={submittingUnit}
                    />
                  </div>
                </>
              )}

              {/* BNB-specific fields */}
              {isBNB && (
                <>
                  <div className="form-group">
                    <label>Price Per Night (KES) *</label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={unitForm.pricePerNight}
                      onChange={(e) => setUnitForm({ ...unitForm, pricePerNight: e.target.value })}
                      required={isBNB}
                      placeholder="e.g., 2500"
                      disabled={submittingUnit}
                    />
                  </div>

                  <div className="form-group">
                    <label>Checkout Time *</label>
                    <input
                      type="text"
                      value={unitForm.checkoutTime}
                      onChange={(e) => setUnitForm({ ...unitForm, checkoutTime: e.target.value })}
                      required={isBNB}
                      placeholder="e.g., 11:00"
                      disabled={submittingUnit}
                    />
                    <small className="form-hint">Format: HH:MM (e.g., 11:00, 14:30)</small>
                  </div>
                </>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddUnit(false)}
                  disabled={submittingUnit}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submittingUnit}
                >
                  {submittingUnit ? 'Creating...' : 'Create Unit'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Bulk Add Units Modal */}
      {showBulkAddUnit && createPortal(
        <div className="modal-overlay" onClick={() => !submittingBulkUnits && setShowBulkAddUnit(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>Bulk Add Units</h2>
              <button className="close-btn" onClick={() => !submittingBulkUnits && setShowBulkAddUnit(false)} disabled={submittingBulkUnits}>×</button>
            </div>
            {bulkUnitErrorMessage && (
              <div style={{ padding: '12px', margin: '16px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>
                {bulkUnitErrorMessage}
              </div>
            )}
            <form onSubmit={handleBulkUnitSubmit} className="modal-form">
              <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Unit Template (Applied to All Units)</h3>

              <div className="form-group">
                <label>Unit Name Template *</label>
                <input
                  type="text"
                  value={bulkUnitForm.name}
                  onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, name: e.target.value })}
                  required
                  placeholder="e.g., 1BR Unit"
                  disabled={submittingBulkUnits}
                />
                <small className="form-hint">Unit numbers will be appended automatically (e.g., "1BR Unit B11")</small>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={bulkUnitForm.description}
                  onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, description: e.target.value })}
                  rows="3"
                  placeholder="e.g., Standard 1 bedroom"
                  disabled={submittingBulkUnits}
                />
              </div>

              <div className="form-group">
                <label>Unit Category *</label>
                <select
                  value={bulkUnitForm.unitCategoryDefinitionId}
                  onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, unitCategoryDefinitionId: e.target.value, unitSubCategoryId: '' })}
                  required
                  disabled={submittingBulkUnits || loadingUnitCategories}
                >
                  <option value="">Select Category</option>
                  {unitCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {loadingUnitCategories && <small className="form-hint">Loading categories...</small>}
              </div>

              <div className="form-group">
                <label>Unit Subcategory *</label>
                <select
                  value={bulkUnitForm.unitSubCategoryId}
                  onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, unitSubCategoryId: e.target.value })}
                  required
                  disabled={submittingBulkUnits || !bulkUnitForm.unitCategoryDefinitionId || loadingUnitSubcategories}
                >
                  <option value="">Select Subcategory</option>
                  {unitSubcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </option>
                  ))}
                </select>
                {loadingUnitSubcategories && <small className="form-hint">Loading subcategories...</small>}
                {!bulkUnitForm.unitCategoryDefinitionId && <small className="form-hint">Please select a category first</small>}
              </div>

              <div className="form-group">
                <label>Unit Type *</label>
                <select
                  value={bulkUnitForm.unitTypeDefinitionId}
                  onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, unitTypeDefinitionId: e.target.value })}
                  required
                  disabled={submittingBulkUnits || loadingUnitTypes}
                >
                  <option value="">Select Unit Type</option>
                  {unitTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {loadingUnitTypes && <small className="form-hint">Loading unit types...</small>}
              </div>

              <div className="form-group">
                <label>Bedrooms</label>
                <input
                  type="number"
                  min="0"
                  value={bulkUnitForm.bedrooms}
                  onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, bedrooms: e.target.value })}
                  placeholder="0"
                  disabled={submittingBulkUnits}
                />
              </div>

              <div className="form-group">
                <label>Bathrooms</label>
                <input
                  type="number"
                  min="0"
                  value={bulkUnitForm.bathrooms}
                  onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, bathrooms: e.target.value })}
                  placeholder="0"
                  disabled={submittingBulkUnits}
                />
              </div>

              <div className="form-group">
                <label>Size (Square Meters)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={bulkUnitForm.sizeSqm}
                  onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, sizeSqm: e.target.value })}
                  placeholder="e.g., 45"
                  disabled={submittingBulkUnits}
                />
              </div>

              <div className="form-group">
                <label>Rent Due Day (Optional)</label>
                <input
                  type="number"
                  min="1"
                  max="28"
                  value={bulkUnitForm.rentDueDay}
                  onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, rentDueDay: e.target.value })}
                  placeholder="1-28 (recommended)"
                  disabled={submittingBulkUnits}
                />
                <small className="form-hint">Optional. Recommended range is 1-28.</small>
              </div>

              <div className="form-group">
                <label>Listing</label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!bulkUnitForm.isPublic}
                    onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, isPublic: e.target.checked })}
                    disabled={submittingBulkUnits}
                    style={{ marginRight: '6px' }}
                  />
                  <span>Public (shown on public listing)</span>
                </label>
              </div>

              <div className="form-group">
                <label>Amenities</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {availableAmenities.map((amenity) => (
                    <label key={amenity} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={bulkUnitForm.amenities.includes(amenity)}
                        onChange={() => toggleBulkUnitAmenity(amenity)}
                        disabled={submittingBulkUnits}
                        style={{ marginRight: '6px' }}
                      />
                      <span style={{ textTransform: 'capitalize' }}>{amenity.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Unit Photos * (2-6 images)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleBulkUnitPhotosChange}
                  disabled={submittingBulkUnits || bulkUnitForm.photoFiles.length >= 6}
                />
                <small className="form-hint">
                  {bulkUnitForm.photoFiles.length}/6 photos selected (minimum 2 required)
                </small>
                {bulkUnitForm.photoPreviews.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '12px' }}>
                    {bulkUnitForm.photoPreviews.map((preview, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #e0e0e0'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveBulkUnitPhoto(index)}
                          disabled={submittingBulkUnits}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            lineHeight: '1'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Rental-specific fields */}
              {bulkIsRental && (
                <>
                  <div className="form-group">
                    <label>Monthly Rent (KES) *</label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={bulkUnitForm.monthlyRent}
                      onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, monthlyRent: e.target.value })}
                      required={bulkIsRental}
                      placeholder="e.g., 15000"
                      disabled={submittingBulkUnits}
                    />
                  </div>

                  <div className="form-group">
                    <label>Deposit Amount (KES) *</label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={bulkUnitForm.depositAmount}
                      onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, depositAmount: e.target.value })}
                      required={bulkIsRental}
                      placeholder="e.g., 15000"
                      disabled={submittingBulkUnits}
                    />
                  </div>
                </>
              )}

              {/* BNB-specific fields */}
              {bulkIsBNB && (
                <>
                  <div className="form-group">
                    <label>Price Per Night (KES) *</label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={bulkUnitForm.pricePerNight}
                      onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, pricePerNight: e.target.value })}
                      required={bulkIsBNB}
                      placeholder="e.g., 2500"
                      disabled={submittingBulkUnits}
                    />
                  </div>

                  <div className="form-group">
                    <label>Checkout Time *</label>
                    <input
                      type="text"
                      value={bulkUnitForm.checkoutTime}
                      onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, checkoutTime: e.target.value })}
                      required={bulkIsBNB}
                      placeholder="e.g., 11:00"
                      disabled={submittingBulkUnits}
                    />
                    <small className="form-hint">Format: HH:MM (e.g., 11:00, 14:30)</small>
                  </div>
                </>
              )}

              <h3 style={{ marginTop: '24px', marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Unit Numbers</h3>

              <div className="form-group">
                <label>Enter Unit Numbers Manually</label>
                <textarea
                  value={bulkUnitForm.unitNumbersInput}
                  onChange={(e) => setBulkUnitForm({ ...bulkUnitForm, unitNumbersInput: e.target.value })}
                  rows="3"
                  placeholder="B11, B12, C11, C12, D11"
                  disabled={submittingBulkUnits}
                />
                <small className="form-hint">Enter comma-separated unit numbers (e.g., B11, B12, C11, C12, D11)</small>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowBulkAddUnit(false)}
                  disabled={submittingBulkUnits}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submittingBulkUnits}
                >
                  {submittingBulkUnits ? 'Creating...' : 'Create Units'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Link Tenant Modal */}
      {showLinkTenant && createPortal(
        <div className="modal-overlay" onClick={() => { if (!linkingTenant) { setShowLinkTenant(null); setLinkTenantError(''); } }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Link Tenant to Unit</h2>
              <button type="button" className="close-btn" onClick={() => { if (!linkingTenant) { setShowLinkTenant(null); setLinkTenantError(''); } }}>×</button>
            </div>
            {linkTenantError && (
              <div style={{ padding: '0 16px', marginTop: '8px', color: '#c33', fontSize: '14px' }}>
                {linkTenantError}
              </div>
            )}
            <form onSubmit={(e) => handleLinkTenant(e, showLinkTenant)} className="modal-form">
              <div className="form-group">
                <label>Tenant Name</label>
                <input
                  type="text"
                  value={tenantForm.tenantName}
                  onChange={(e) => setTenantForm({ ...tenantForm, tenantName: e.target.value })}
                  required
                  placeholder="Enter tenant's full name (e.g., Kingsley Ochieng)"
                />
              </div>
              <div className="form-group">
                <label>Tenant ID</label>
                <input
                  type="text"
                  value={tenantForm.tenantId}
                  onChange={(e) => setTenantForm({ ...tenantForm, tenantId: e.target.value })}
                  required
                  placeholder="Enter tenant ID (e.g., T001, G001)"
                />
                <small className="form-hint">
                  Enter the tenant's ID that was assigned during sign up. This ID is used to track the tenant's rental history and score.
                </small>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { if (!linkingTenant) { setShowLinkTenant(null); setLinkTenantError(''); } }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={linkingTenant}>
                  {linkingTenant ? 'Linking…' : 'Link Tenant'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Rent Settings Modal */}
      {/* Update Unit Modal */}
      {showUpdateUnit && createPortal(
        <div className="modal-overlay" onClick={() => !updatingUnit && setShowUpdateUnit(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>Update Unit</h2>
              <button className="close-btn" onClick={() => !updatingUnit && setShowUpdateUnit(null)} disabled={updatingUnit}>×</button>
            </div>
            {updateUnitErrorMessage && (
              <div style={{ padding: '12px', margin: '16px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>
                {updateUnitErrorMessage}
              </div>
            )}
            <form onSubmit={handleUpdateUnit} className="modal-form">
              <div className="form-group">
                <label>Unit Number</label>
                <input
                  type="text"
                  value={updateUnitForm.unitNumber}
                  onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, unitNumber: e.target.value })}
                  placeholder="e.g., A1, 101"
                  disabled={updatingUnit}
                />
              </div>

              <div className="form-group">
                <label>Unit Name</label>
                <input
                  type="text"
                  value={updateUnitForm.name}
                  onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, name: e.target.value })}
                  placeholder="e.g., Unit A1 - Single Room"
                  disabled={updatingUnit}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={updateUnitForm.description}
                  onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, description: e.target.value })}
                  rows="3"
                  placeholder="e.g., Spacious 1BR with balcony"
                  disabled={updatingUnit}
                />
              </div>

              <div className="form-group">
                <label>Unit Category</label>
                <select
                  value={updateUnitForm.unitCategoryDefinitionId}
                  onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, unitCategoryDefinitionId: e.target.value, unitSubCategoryId: '' })}
                  disabled={updatingUnit || loadingUnitCategories}
                >
                  <option value="">Select Category</option>
                  {unitCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {loadingUnitCategories && <small className="form-hint">Loading categories...</small>}
              </div>

              <div className="form-group">
                <label>Unit Subcategory</label>
                <select
                  value={updateUnitForm.unitSubCategoryId}
                  onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, unitSubCategoryId: e.target.value })}
                  disabled={updatingUnit || !updateUnitForm.unitCategoryDefinitionId || loadingUnitSubcategories}
                >
                  <option value="">Select Subcategory</option>
                  {unitSubcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </option>
                  ))}
                </select>
                {loadingUnitSubcategories && <small className="form-hint">Loading subcategories...</small>}
                {!updateUnitForm.unitCategoryDefinitionId && <small className="form-hint">Please select a category first</small>}
              </div>

              <div className="form-group">
                <label>Unit Type</label>
                <select
                  value={updateUnitForm.unitTypeDefinitionId}
                  onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, unitTypeDefinitionId: e.target.value })}
                  disabled={updatingUnit || loadingUnitTypes}
                >
                  <option value="">Select Unit Type</option>
                  {unitTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {loadingUnitTypes && <small className="form-hint">Loading unit types...</small>}
              </div>

              <div className="form-group">
                <label>Unit Photos (2-6 images)</label>
                {/* Show existing photos */}
                {updateUnitForm.existingPhotos.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
                    {updateUnitForm.existingPhotos.map((photoUrl, index) => {
                      // Use authenticated image if available, otherwise fallback to original URL
                      const photoKey = `photo_${index}`;
                      const displayUrl = authenticatedUnitPhotos[photoKey] || photoUrl;

                      return (
                        <div key={index} style={{ position: 'relative' }}>
                          <img
                            src={displayUrl}
                            alt={`Existing ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100px',
                              objectFit: 'cover',
                              borderRadius: 8,
                              border: '1px solid #e0e0e0'
                            }}
                            onError={(e) => {
                              const el = e.currentTarget;
                              if (displayUrl !== photoUrl) {
                                el.onerror = () => {
                                  el.onerror = null;
                                  el.src = BROKEN_IMAGE_PLACEHOLDER_SRC;
                                };
                                el.src = photoUrl;
                                return;
                              }
                              el.onerror = null;
                              el.src = BROKEN_IMAGE_PLACEHOLDER_SRC;
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveUpdateUnitPhoto(index, true)}
                            disabled={updatingUnit}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              background: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              cursor: 'pointer',
                              fontSize: '16px',
                              lineHeight: '1'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Show new photo previews */}
                {updateUnitForm.photoPreviews.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
                    {updateUnitForm.photoPreviews.map((preview, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #e0e0e0'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveUpdateUnitPhoto(index, false)}
                          disabled={updatingUnit}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            lineHeight: '1'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleUpdateUnitPhotosChange}
                  disabled={updatingUnit || (updateUnitForm.existingPhotos.length + updateUnitForm.photoFiles.length) >= 6}
                />
                <small className="form-hint">
                  {(updateUnitForm.existingPhotos.length + updateUnitForm.photoFiles.length)}/6 photos selected (minimum 2 required if adding photos)
                </small>
              </div>

              {/* Determine if unit is Rental or BNB based on existing unit data */}
              {(() => {
                const isRental = selectedUnitForUpdate?.monthlyRent !== null && selectedUnitForUpdate?.monthlyRent !== undefined;
                const isBNB = selectedUnitForUpdate?.pricePerNight !== null && selectedUnitForUpdate?.pricePerNight !== undefined;

                return (
                  <>
                    {/* Rental-specific fields - show if unit has monthlyRent */}
                    {isRental && (
                      <>
                        <div className="form-group">
                          <label>Monthly Rent (KES)</label>
                          <input
                            type="number"
                            min="0"
                            step="100"
                            value={updateUnitForm.monthlyRent}
                            onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, monthlyRent: e.target.value })}
                            placeholder="e.g., 15000"
                            disabled={updatingUnit}
                          />
                        </div>

                        <div className="form-group">
                          <label>Deposit Amount (KES)</label>
                          <input
                            type="number"
                            min="0"
                            step="100"
                            value={updateUnitForm.depositAmount}
                            onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, depositAmount: e.target.value })}
                            placeholder="e.g., 15000"
                            disabled={updatingUnit}
                          />
                        </div>

                        <div className="form-group">
                          <label>Rent Due Day (Optional)</label>
                          <input
                            type="number"
                            min="1"
                            max="28"
                            value={updateUnitForm.rentDueDay}
                            onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, rentDueDay: e.target.value })}
                            placeholder="1-28 (recommended)"
                            disabled={updatingUnit}
                          />
                          <small className="form-hint">Optional. Recommended range is 1-28.</small>
                        </div>
                      </>
                    )}

                    {/* BNB-specific fields - show if unit has pricePerNight */}
                    {isBNB && (
                      <>
                        <div className="form-group">
                          <label>Price Per Night (KES)</label>
                          <input
                            type="number"
                            min="0"
                            step="100"
                            value={updateUnitForm.pricePerNight}
                            onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, pricePerNight: e.target.value })}
                            placeholder="e.g., 2500"
                            disabled={updatingUnit}
                          />
                        </div>

                        <div className="form-group">
                          <label>Checkout Time</label>
                          <input
                            type="text"
                            value={updateUnitForm.checkoutTime}
                            onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, checkoutTime: e.target.value })}
                            placeholder="e.g., 11:00"
                            disabled={updatingUnit}
                          />
                          <small className="form-hint">Format: HH:MM (e.g., 11:00, 14:30)</small>
                        </div>
                      </>
                    )}

                    <div className="form-group">
                      <label>Listing</label>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!updateUnitForm.isPublic}
                          onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, isPublic: e.target.checked })}
                          disabled={updatingUnit}
                          style={{ marginRight: '6px' }}
                        />
                        <span>Public (shown on public listing)</span>
                      </label>
                    </div>
                  </>
                );
              })()}

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={updateUnitForm.isAvailable}
                    onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, isAvailable: e.target.checked })}
                    disabled={updatingUnit}
                    style={{ marginRight: '8px' }}
                  />
                  Unit is Available
                </label>
                <small className="form-hint">Check this if the unit is currently available for rent/booking</small>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowUpdateUnit(null)}
                  disabled={updatingUnit}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updatingUnit}
                >
                  {updatingUnit ? 'Updating...' : 'Update Unit'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Unit Confirmation Modal */}
      {unitToDelete && createPortal(
        <div className="modal-overlay" onClick={handleCancelDeleteUnit}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Unit</h2>
              <button className="close-btn" onClick={handleCancelDeleteUnit} disabled={deletingUnit}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this unit? This action cannot be undone.</p>
              <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
                All associated data for this unit will be permanently removed.
              </p>
              {deleteUnitError && (
                <p style={{ fontSize: '14px', color: '#c33', marginTop: '12px' }}>
                  {deleteUnitError}
                </p>
              )}
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={handleCancelDeleteUnit} disabled={deletingUnit}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmDeleteUnit} disabled={deletingUnit}>
                {deletingUnit ? 'Deleting...' : 'Delete Unit'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {evictionModal.open && createPortal(
        <div className="modal-overlay" onClick={closeEvictionModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Issue Eviction Notice</h2>
              <button className="close-btn" onClick={closeEvictionModal} disabled={Boolean(submittingEvictionId)}>×</button>
            </div>
            <div className="modal-form">
              <div className="form-group">
                <label>Tenant</label>
                <input type="text" value={evictionModal.tenantName || ''} disabled />
              </div>
              <div className="form-group">
                <label>Unit</label>
                <input type="text" value={evictionModal.unitLabel || ''} disabled />
              </div>
              <div className="form-group">
                <label>Move-out Date *</label>
                <input
                  type="date"
                  value={evictionModal.moveOutDate}
                  onChange={(e) => setEvictionModal((prev) => ({ ...prev, moveOutDate: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Reason *</label>
                <textarea
                  rows="4"
                  value={evictionModal.reason}
                  onChange={(e) => setEvictionModal((prev) => ({ ...prev, reason: e.target.value }))}
                  placeholder="Breach of house rules (noise complaints)"
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeEvictionModal} disabled={Boolean(submittingEvictionId)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={submitEvictionNotice} disabled={Boolean(submittingEvictionId)}>
                  {submittingEvictionId ? 'Submitting...' : 'Issue Eviction'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Release Tenant Modal */}
      {releaseModal.open && createPortal(
        <div className="modal-overlay" onClick={closeReleaseModal}>
          <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Release Tenant</h2>
              <button className="close-btn" onClick={closeReleaseModal} disabled={Boolean(submittingReleaseId)}>×</button>
            </div>
            <div className="modal-form">
              <div className="form-group">
                <label>Tenant</label>
                <input type="text" value={releaseModal.tenantName || ''} disabled />
              </div>
              <div className="form-group">
                <label>Unit</label>
                <input type="text" value={releaseModal.unitLabel || ''} disabled />
              </div>
              <div className="form-group">
                <label>Release Date (Optional)</label>
                <input
                  type="date"
                  value={releaseModal.releaseDate}
                  onChange={(e) => setReleaseModal((prev) => ({ ...prev, releaseDate: e.target.value }))}
                />
                <small className="form-hint">Leave empty or set to today to release them immediately.</small>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeReleaseModal} disabled={Boolean(submittingReleaseId)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={submitReleaseTenant} disabled={Boolean(submittingReleaseId)}>
                  {submittingReleaseId ? 'Submitting...' : 'Release Tenant'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bulk Edit Units Modal */}
      {showBulkEdit && createPortal(
        <div className="modal-overlay" onClick={() => setShowBulkEdit(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Bulk Edit Units</h2>
              <button className="close-btn" onClick={() => setShowBulkEdit(false)}>×</button>
            </div>
            <form onSubmit={handleBulkEdit} className="modal-form">
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={bulkEditForm.applyToAll}
                    onChange={(e) => setBulkEditForm({ ...bulkEditForm, applyToAll: e.target.checked })}
                  />{' '}
                  Apply to all units in this property
                </label>
                {!bulkEditForm.applyToAll && (
                  <small className="form-hint">
                    Currently editing {selectedUnitIds.length} selected unit(s)
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Change Monthly Rent (KES) (Optional)</label>
                <input
                  type="number"
                  value={bulkEditForm.rent}
                  onChange={(e) => setBulkEditForm({ ...bulkEditForm, rent: e.target.value })}
                  placeholder="Enter new rent amount"
                  min="0"
                  step="100"
                />
                <small className="form-hint">
                  Enter a new rent amount to apply to all selected units. Leave empty to keep current rent.
                </small>
              </div>

              <div className="form-group">
                <label>Change Unit Type (Optional)</label>
                <select
                  value={bulkEditForm.unitType}
                  onChange={(e) => setBulkEditForm({ ...bulkEditForm, unitType: e.target.value })}
                >
                  <option value="">-- Keep Current --</option>
                  <option value="studio">Studio / Bedsitter</option>
                  <option value="1-bedroom">1 Bedroom</option>
                  <option value="2-bedroom">2 Bedroom</option>
                  <option value="3-bedroom">3 Bedroom</option>
                  <option value="4-bedroom">4 Bedroom</option>
                  <option value="5-bedroom">5 Bedroom</option>
                  <option value="commercial">Commercial</option>
                  <option value="other">Other</option>
                </select>
                <small className="form-hint">
                  Use this to correct unit types in bulk (e.g. change bedsitters to 1-bedroom).
                </small>
              </div>

              <div className="form-group">
                <label>Update Unit Number (Optional)</label>
                <div className="form-row">
                  <div className="form-group">
                    <label>Prefix</label>
                    <input
                      type="text"
                      value={bulkEditForm.unitNumberPrefix}
                      onChange={(e) =>
                        setBulkEditForm({ ...bulkEditForm, unitNumberPrefix: e.target.value })
                      }
                      placeholder="e.g., A, B, 1"
                    />
                    <small className="form-hint">Add or replace prefix (e.g., A for A1, A2, A3)</small>
                  </div>
                  <div className="form-group">
                    <label>Suffix</label>
                    <input
                      type="text"
                      value={bulkEditForm.unitNumberSuffix}
                      onChange={(e) =>
                        setBulkEditForm({ ...bulkEditForm, unitNumberSuffix: e.target.value })
                      }
                      placeholder="e.g., -A, -B"
                    />
                    <small className="form-hint">Add suffix to unit numbers</small>
                  </div>
                </div>
                <small
                  className="form-hint"
                  style={{ marginTop: '8px', display: 'block' }}
                >
                  Example: If unit is "1" and you add prefix "A", it becomes "A1". If you add suffix
                  "-A", it becomes "1-A".
                </small>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowBulkEdit(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-sm">
                  Apply Changes
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
      {/* Tenant Rating Modal */}
      {!editModalOnly && (
      <TenantRatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        tenant={selectedTenantForRating}
        propertyId={propertyId}
        unitId={selectedTenantForRating?.id}
        onRatingSuccess={(msg) => {
          setRatingSuccessMessage(msg);
          setTimeout(() => setRatingSuccessMessage(''), 5000);
        }}
      />
      )}

      {!editModalOnly && ratingSuccessMessage && (
        <div className="rating-success-toast">
          {ratingSuccessMessage}
        </div>
      )}
    </>
  );
};

export default PropertyDetails;
